import React, { useState, useEffect, useContext, useRef } from "react";
import "./RaiseBill.scss";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toast";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import config from "../../config/config";
import {
  FiChevronDown,
  FiPlus,
  FiMinus,
  FiX,
  FiEdit2,
  FiTrash2,
  FiSave,
  FiArrowLeft,
  FiShare2,
  FiCheck,
  FiXCircle,
  FiFileText,
  FiMenu,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";
import { TbLayersDifference } from "react-icons/tb";
import ImageGrid from "./Components/ImageGrid";
import { MdDrafts } from "react-icons/md";
import drawerTemplate from "../../hoc/drawerTemplate";
import { UserContext } from "../../Context/UserContext";
import OrdersBillingAPI from "../../api/ordersBillingAPI";
import ProgressIndicator from "../../components/ProgressIndicator/ProgressIndicator";
import PaymentMilestones from "./Components/PaymentMilestones";
import { TbCameraPlus } from "react-icons/tb";
import MultipleImageUploader from "./Components/MultipleImageUploader";
import StatusProgress from "../Billing/Components/StatusProgress";
import BillComparisonView from "./Components/BillComparionView";
import "react-responsive-modal/styles.css";
import { Modal } from "react-responsive-modal";
const RaiseBill = () => {
  const userContext = useContext(UserContext);
  const history = useHistory();
  const location = useLocation();
  const billData = location.state?.data || {};
  const modalStyles = {
    modal: {
      background: "white",
      borderRadius: "8px",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
      padding: "20px",
      maxWidth: "450px",
      width: "100%",
    },
  };
  // Determine mode: viewMode (readonly) or editMode
  const viewMode = billData.viewMode || false;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };
  // Extract data from passed state
  const projectId = billData.ProjectId || "";
  const projectName = billData.ProjectName || "";
  const serviceOrder = billData.serviceOrder || {};
  const poId = billData.poId || serviceOrder.poId || "";
  const clientInfo = billData.client || serviceOrder.client || {};
  const vendorInfo = billData.vendor || serviceOrder.vendor || {};
  const selectedItems = serviceOrder.selectedItems || [];
  const billId = billData.billId || generateId();

  // If bill is being viewed, use the full bill data
  const existingBillData = billData.billData || {};
  const [workOrderImages, setWorkOrderImages] = useState(
    existingBillData.workOrderImages || {}
  );

  const [additionalImages, setAdditionalImages] = useState(
    existingBillData.additionalImages || {}
  );

  const [paidAmount, setPaidAmount] = useState(
    existingBillData.paidAmount || 0
  );

  const [totalPaidAmount, setTotalPaidAmount] = useState(
    existingBillData.totalPaidAmount || 0
  );

  const [showModal, setShowModal] = useState(false);

  const handleModalClose = () => {
    setShowModal(false);
  };
  const handleModalOpen = () => {
    setShowModal(true);
  };

  const handlePaidAmountSubmit = async () => {
    console.log("Paid Amount:", paidAmount);
    try {
      setIsSubmitting(true);
      setLoadingMessage("Saving bill...");
      setLoadingProgress(30);

      // Format data for API using the helper function
      const formattedData = convertToApiFormatForPaidAmount();

      // If editing an approved bill and changes were made, change status to Requested
      if (billStatus === "Approved" && isEditingApprovedBill && hasChanges) {
        formattedData.status = "Requested";
        setBillStatus("Requested");
        toast.info("Bill status changed to Requested for re-approval");
      }

      setLoadingProgress(60);

      // Make the actual API call
      let response;
      if (viewMode && existingBillData) {
        // Update existing bill
        response = await OrdersBillingAPI.UpdateBillingInformation(
          poId,
          billId,
          formattedData
        );
      } else {
        // Create new bill
        response = await OrdersBillingAPI.SaveBill(poId, billId, formattedData);
      }

      setLoadingProgress(90);

      if (response && response.isError) {
        throw new Error(response.errorMessage || "Failed to save bill");
      }

      // Reset edit mode and changes flag after successful save
      if (isEditingApprovedBill) {
        setIsEditingApprovedBill(false);
        setHasChanges(false);
      }
      handleModalClose();
      setLoadingProgress(100);
      setLoadingMessage("Bill saved successfully!");

      toast.success("Bill saved successfully!");
      setTimeout(() => {
        history.push("/Billing");
      }, 1000);
    } catch (error) {
      console.error("Error saving bill:", error);
      toast.error("Error saving bill: " + (error.message || "Unknown error"));
    } finally {
      setIsSubmitting(false);
      setLoadingMessage("");
      setLoadingProgress(0);
    }
  };

  // Build current date if not present in data
  function getCurrentDateFormatted() {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = now.getFullYear();
    return `${day}-${month}-${year}`;
  }
  const handleUploadCompleteForWorkOrder = (uploadedImagesObj) => {
    setWorkOrderImages((prevImages) => ({
      ...prevImages,
      ...uploadedImagesObj,
    }));
  };

  const handleDeleteImageForWorkOrder = (index) => {
    console.log("Deleting image at index:", index);
    setWorkOrderImages((prevImages) => {
      const entries = Object.entries(prevImages);
      const keyToDelete = entries[index]?.[0]; // Get the key at that index
      if (!keyToDelete) return prevImages; // safety check

      const updatedImages = { ...prevImages };
      delete updatedImages[keyToDelete];
      return updatedImages;
    });
  };

  const handleUploadCompleteForAdditional = (uploadedImagesObj) => {
    setAdditionalImages((prevImages) => ({
      ...prevImages,
      ...uploadedImagesObj,
    }));
  };

  const handleDeleteImageForAdditional = (index) => {
    setAdditionalImages((prevImages) => {
      const entries = Object.entries(prevImages);
      const keyToDelete = entries[index]?.[0]; // Get the key at that index
      if (!keyToDelete) return prevImages; // safety check
      const updatedImages = { ...prevImages };
      delete updatedImages[keyToDelete];
      return updatedImages;
    });
  };
  // Form fields - set from existing data if available
  const [billNumber, setBillNumber] = useState(
    existingBillData.billNo || generateBillNumber()
  );
  const [billDate, setBillDate] = useState(
    existingBillData.date || getCurrentDateFormatted()
  );

  const [billType, setBillType] = useState(
    existingBillData.billType ||
      billData.billType ||
      serviceOrder.billType ||
      "Work Bill"
  );
  const [billStatus, setBillStatus] = useState(
    existingBillData.status || "Requested"
  );

  const [compareBill, setCompareBill] = useState(false);

  // Use a separate state for readOnly
  const [readOnly, setReadOnly] = useState(
    viewMode ? billData.readOnly === true : false
  );

  const [isEditingApprovedBill, setIsEditingApprovedBill] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const confirmOverwrite = async () => {
    try {
      setIsSubmitting(true);
      setLoadingMessage("Saving bill...");
      setLoadingProgress(30);

      // Format data for API using the helper function
      const formattedData = convertToApiFormatForBilledAmount();

      // If editing an approved bill and changes were made, change status to Requested
      if (billStatus === "Approved" && isEditingApprovedBill && hasChanges) {
        formattedData.status = "Requested";
        setBillStatus("Requested");
        toast.info("Bill status changed to Requested for re-approval");
      }

      setLoadingProgress(60);

      // Make the actual API call
      let response;
      if (viewMode && existingBillData) {
        // Update existing bill
        response = await OrdersBillingAPI.UpdateBillingInformation(
          poId,
          billId,
          formattedData
        );
      } else {
        // Create new bill
        response = await OrdersBillingAPI.SaveBill(poId, billId, formattedData);
      }

      setLoadingProgress(90);

      if (response && response.isError) {
        throw new Error(response.errorMessage || "Failed to save bill");
      }

      // Reset edit mode and changes flag after successful save
      if (isEditingApprovedBill) {
        setIsEditingApprovedBill(false);
        setHasChanges(false);
      }

      setLoadingProgress(100);
      setLoadingMessage("Bill saved successfully!");

      toast.success("Bill saved successfully!");
      setTimeout(() => {
        history.push("/Billing");
      }, 1000);
    } catch (error) {
      console.error("Error saving bill:", error);
      toast.error("Error saving bill: " + (error.message || "Unknown error"));
    } finally {
      setIsSubmitting(false);
      setLoadingMessage("");
      setLoadingProgress(0);
    }
  };
  // Update readOnly value whenever billStatus changes
  useEffect(() => {
    // Allow editing for Requested, Declined, and Approved bills in edit mode
    const newReadOnlyValue = !(
      billStatus === "Requested" ||
      billStatus === "Declined" ||
      billStatus === "Draft" ||
      isEditingApprovedBill
    );
    setReadOnly(newReadOnlyValue);
  }, [billStatus, isEditingApprovedBill]);

  // Client section fields
  const [clientDetails, setClientDetails] = useState({
    clientName: clientInfo.name || projectName || "",
    location: clientInfo.location || "",
    projectId: projectId || "",
  });

  // Vendor section fields
  const [vendorDetails, setVendorDetails] = useState({
    name: billData.vendorName || "",
    firm: billData.vendorFirm || "",
    category: existingBillData.vendor?.category || vendorInfo.category || "",
  });

  // Person details fields
  const [personDetails, setPersonDetails] = useState({
    name:
      existingBillData.personDetails?.name ||
      userContext.user?.DisplayName ||
      "",
    category:
      existingBillData.personDetails?.category || vendorInfo.category || "",
    mobileNo:
      existingBillData.personDetails?.mobileNo || billData?.vendorMobile || "",
    gstNo:
      existingBillData.personDetails?.gstNo ||
      billData?.bankAccountDetails?.GSTIN ||
      "",
    panNo:
      existingBillData.personDetails?.panNo ||
      billData?.bankAccountDetails?.PAN ||
      "",
  });

  // Payment details fields - extract from PO or use defaults
  const [paymentDetails, setPaymentDetails] = useState({
    bankName: existingBillData.paymentDetails?.bankName || "",
    ifscCode:
      existingBillData.paymentDetails?.ifscCode ||
      billData?.bankAccountDetails?.IFSC ||
      "",
    accountNo:
      existingBillData.paymentDetails?.accountNo ||
      billData?.bankAccountDetails?.AccountNumber ||
      "",
    poId: poId || (serviceOrder.Id ? serviceOrder.Id.toString() : ""),
    aadharNo:
      existingBillData.paymentDetails?.aadharNo ||
      billData?.bankAccountDetails?.AADHAR ||
      "",
  });

  // Work order sections - initialize empty then fill dynamically
  const [sections, setSections] = useState([
    {
      id: "workOrderItems",
      title: "WORK ORDER ITEMS",
      isExpanded: true,
      subsections: [],
    },
    {
      id: "additionalItems",
      title: "ADDITIONAL ITEMS (NON ADDITIONAL WORK ORDER ITEM)",
      isExpanded: true,
      subsections: [],
    },
    {
      id: "mileStones",
      title: "MILE STONES",
      isExpanded: true,
      subsections: [],
    },
  ]);

  // Store all related bills for this PO
  const [relatedBills, setRelatedBills] = useState(billData.relatedBills || []);
  const [currentBillIndex, setCurrentBillIndex] = useState(
    typeof billData.currentBillIndex === "number"
      ? billData.currentBillIndex
      : 0
  );
  const [showAllBills, setShowAllBills] = useState(false);
  const [showTotalView, setShowTotalView] = useState(false);
  const [billsSummary, setBillsSummary] = useState({
    totalAmount: 0,
    totalPaid: 0,
    totalDue: 0,
  });
  const [billedAmount, setBilledAmount] = useState(
    existingBillData.billedAmount || 0
  ); // Bill summary - calculate based on sections or use existing data
  const [billSummary, setBillSummary] = useState({
    previousBill: existingBillData.previousBill || 0,
    presentBill: existingBillData.presentBill || 0,
    totalAmount: existingBillData.totalAmount || 0,
    retentionPercentage: existingBillData.retentionPercentage || 0,
    retentionMoney: existingBillData.retentionMoney || 0,
    previousTotalAmount: existingBillData.previousTotalAmount || 0,
    alreadyPaidAmount: 0, // This will be calculated correctly by updatePreviousBillAmount
    billedAmount: existingBillData.billedAmount || 0,
  }); // Handle when paidAmount changes - call updatePreviousBillAmount to recalculate alreadyPaidAmount
  useEffect(() => {
    // When paidAmount changes, update the already paid amount by calling updatePreviousBillAmount
    if (relatedBills && relatedBills.length > 0) {
      updatePreviousBillAmount();
    }
  }, [paidAmount]);
  // Handle when billedAmount changes - this updates the presentBill in billSummary
  useEffect(() => {
    // When billedAmount changes, update both the presentBill and totalAmount in the billSummary
    setBillSummary((prevSummary) => {
      const presentBillValue = parseFloat(billedAmount) || 0;
      const previousBillValue = parseFloat(prevSummary.previousBill) || 0;
      const totalAmountValue = previousBillValue + presentBillValue;
      const retentionPercentage =
        parseFloat(prevSummary.retentionPercentage) || 0;
      const retentionMoney = (presentBillValue * retentionPercentage) / 100;
      const alreadyPaidAmount = parseFloat(prevSummary.alreadyPaidAmount) || 0;
      const netPayable = totalAmountValue - retentionMoney - totalPaidAmount;

      return {
        ...prevSummary,
        presentBill: presentBillValue,
        totalAmount: totalAmountValue,
        retentionMoney: retentionMoney,
        netPayable: netPayable,
      };
    });
  }, [billedAmount]);

  // Final remark
  const [finalRemark, setFinalRemark] = useState(
    existingBillData.remarks || ""
  );

  // Approval details - use existing data or defaults
  const [approvalDetails, setApprovalDetails] = useState({
    raisedBy:
      existingBillData.raisedBy || userContext.user?.DisplayName || "System",
    raisedDate: existingBillData.raisedDate || getCurrentDateFormatted(),
    checkedBy: existingBillData.checkedBy || "",
    checkedDate: existingBillData.checkedDate || "",
    approvedBy: existingBillData.approvedBy || "",
    approvedDate: existingBillData.approvedDate || "",
  });

  // UI state
  const [editingField, setEditingField] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isDraggingDisabled, setIsDraggingDisabled] = useState(false);
  const [showApproveDecline, setShowApproveDecline] = useState(false);

  // Effect to handle visibility of paid bills
  useEffect(() => {
    // Show approve/decline buttons ONLY for existing bills in Requested or Declined status and not readonly
    if (
      viewMode &&
      (billStatus === "Requested" || billStatus === "Declined") &&
      !readOnly
    ) {
      setShowApproveDecline(true);
    } else {
      setShowApproveDecline(false);
    }
  }, [billStatus, readOnly, viewMode]);

  // Add a ref to track initialization
  const isInitialized = useRef(false);

  // Effect to create subsections from PO data or existing bill data
  useEffect(() => {
    // Prevent this effect from running multiple times with the same data
    if (isInitialized.current) {
      return;
    }

    isInitialized.current = true;

    if (viewMode && existingBillData) {
      // For view mode, load from existing bill data
      loadFromExistingBill(existingBillData);

      // Set the correct bill status
      setBillStatus(existingBillData.status || "Requested");

      // Log for debugging
    } else if (selectedItems && selectedItems.length > 0) {
      // For new bill, create from selected items
      // Pass the reference bill data to help with item matching
      createSubsectionsFromSelectedItems(
        selectedItems,
        billData.referenceBillData
      );
    } else {
      // Default data for testing/development
      console.log("No items selected, using sample data");
      initializeWithSampleData();
    }
  }, [existingBillData, currentBillIndex]); // Effect to recalculate bill summary total whenever bills change
  useEffect(() => {
    if (relatedBills.length > 0) {
      let totalAmount = 0;
      let totalPaid = 0;
      let totalDue = 0;

      relatedBills.forEach((bill) => {
        const amount = parseFloat(bill.totalAmount || 0);
        // Use the explicit paidAmount if available, otherwise 0 (don't assume paid)
        const amountPaid = parseFloat(bill.paidAmount || 0);

        totalAmount += amount;

        // Calculate paid and due amounts properly
        if (bill.status === "Paid") {
          // For paid bills, add the paidAmount to totalPaid
          totalPaid += amountPaid;

          // Add remaining amount (if any) to totalDue
          const remainingDue = amount - amountPaid;
          if (remainingDue > 0) {
            totalDue += remainingDue;
          }
        } else {
          // For any other status, the full amount is due
          totalDue += amount;
        }
      });

      setBillsSummary({
        totalAmount,
        totalPaid,
        totalDue,
      });

      // Update previous bill amount and already paid amounts from all previous bills
      updatePreviousBillAmount();
    }
  }, [relatedBills, currentBillIndex, billedAmount, paidAmount]); // Function to sum up all previous bill amounts and update bill summary
  const updatePreviousBillAmount = () => {
    if (relatedBills.length > 0) {
      // Calculate the sum of all previous bills
      let totalPreviousAmount = 0;
      let totalPaidAmount = 0;

      // Loop through all previous bills (all bills with index less than currentBillIndex)
      for (let i = 0; i < currentBillIndex; i++) {
        const bill = relatedBills[i];
        if (bill) {
          // Add this bill's amount to the total
          const billAmount = parseFloat(
            bill.presentBill || bill.totalAmount || 0
          );
          totalPreviousAmount += billAmount;

          // Add paid amounts for ALL bills that are marked as Paid
        }
      }
      for (let i = 0; i <= currentBillIndex; i++) {
        const bill = relatedBills[i];
        if (bill) {
          // Add this bill's amount to the total

          // Add paid amounts for ALL bills that are marked as Paid
          if (bill.status === "Paid") {
            // Use the paidAmount if available, otherwise use the total amount
            // This assumes fully paid if no specific paid amount is provided
            const paidAmount = parseFloat(
              bill.paidAmount || bill.totalAmount || 0
            );
            totalPaidAmount += paidAmount;
          }
        }
      }
      console.log(
        "Calculated total paid amount from previous bills:",
        totalPaidAmount
      );

      setTotalPaidAmount(totalPaidAmount);

      // Update bill summary with the total of all previous bills and paid amounts
      setBillSummary((prev) => {
        const presentBillValue =
          parseFloat(billedAmount || prev.presentBill) || 0;
        const retentionPercentage = parseFloat(prev.retentionPercentage) || 0;
        const retentionMoney = (presentBillValue * retentionPercentage) / 100;
        const netPayable =
          totalPreviousAmount +
          presentBillValue -
          retentionMoney -
          totalPaidAmount;

        return {
          ...prev,
          previousBill: totalPreviousAmount,
          previousTotalAmount: totalPreviousAmount,
          alreadyPaidAmount: totalPaidAmount, // Set to the sum of all previous paid amounts
          presentBill: presentBillValue,
          totalAmount: totalPreviousAmount + presentBillValue,
          retentionMoney: retentionMoney,
          netPayable: netPayable,
        };
      });
    }
  };

  // Load subsections from an existing bill
  const loadFromExistingBill = (billData) => {
    try {
      // Load basic bill details
      setBillNumber(billData.billNo || generateBillNumber());
      setBillDate(billData.date || formatCurrentDate());
      setBillType(billData.billType || "Work Order");

      // Set bill status explicitly from the data
      setBillStatus(billData.status || "Requested");

      // Load client details
      setClientDetails({
        clientName: billData.client?.name || "",
        location: billData.client?.location || "",
      });

      // Load vendor details
      setVendorDetails({
        name: billData.vendor?.name || "",
        firm: billData.vendor?.firm || "",
        category: billData.vendor?.category || "",
      }); // Set current bill index if relatedBills are provided
      if (relatedBills && relatedBills.length > 0) {
        // Find the index of the current bill in the related bills array
        const index = relatedBills.findIndex(
          (bill) => bill.billId === billData.billId
        );
        if (index !== -1) {
          setCurrentBillIndex(index);

          // Wait for the next render cycle to ensure currentBillIndex has been updated
          setTimeout(() => {
            updatePreviousBillAmount(); // Calculate already paid amount based on previous bills
          }, 0);
        }
      }

      // Load person details
      setPersonDetails(billData.personDetails || {});

      // Load payment details
      setPaymentDetails(billData.paymentDetails || {});

      // Load approval details
      setApprovalDetails({
        raisedBy: billData.raisedBy || personDetails.name || "",
        raisedDate: billData.raisedAt || formatCurrentDate(),
        checkedBy: billData.checkedBy || "",
        checkedDate: billData.checkedAt || "",
        approvedBy: billData.approvedBy || "",
        approvedDate: billData.approvedAt || "",
      });

      setPaidAmount(parseFloat(billData.paidAmount) || 0);
      setBilledAmount(parseFloat(billData.billedAmount) || 0);
      setWorkOrderImages(billData.workOrderImages || {});
      setAdditionalImages(billData.additionalImages || {}); // We'll leave updating alreadyPaidAmount to the updatePreviousBillAmount function
      // This just sets other fields from billData
      setBillSummary((prev) => ({
        ...prev,
        previousBill: parseFloat(billData.previousBill) || 0,
        presentBill: parseFloat(billData.presentBill) || 0,
        totalAmount: parseFloat(billData.totalAmount) || 0,
        retentionPercentage: parseFloat(billData.retentionPercentage) || 0,
        retentionMoney: parseFloat(billData.retentionMoney) || 0,
        previousTotalAmount: parseFloat(billData.previousTotalAmount) || 0,
        // Include totalPaidAmount when setting alreadyPaidAmount
        alreadyPaidAmount: parseFloat(billData.totalPaidAmount) || 0,
        netPayable: parseFloat(totalPaidAmount) || 0,
      }));

      // Calculate the present bill amount based on the sections data
      const presentBillAmount = calculateTotalFromBillData(billData);

      // Set previous bill amount - either from data or from previous bill in sequence
      let previousBillAmount = parseFloat(billData.previousBill) || 0;

      // If we have related bills and this isn't the first bill, get amount from previous bill
      if (relatedBills && relatedBills.length > 0) {
        const index = relatedBills.findIndex(
          (bill) => bill.billId === billData.billId
        );
        if (index > 0) {
          previousBillAmount =
            parseFloat(relatedBills[index - 1].totalAmount) || 0;
        }
      } // Load bill summary with the already paid amount from totalPaidAmount
      const alreadyPaidAmount =
        parseFloat(billData.totalPaidAmount) ||
        parseFloat(totalPaidAmount) ||
        0;
      const retentionPercent = parseFloat(billData.retentionPercentage) || 0;
      const retentionAmount = (presentBillAmount * retentionPercent) / 100;

      setBillSummary({
        previousBill: previousBillAmount,
        presentBill: presentBillAmount,
        totalAmount: previousBillAmount + presentBillAmount,
        retentionPercentage: retentionPercent,
        retentionMoney: retentionAmount,
        previousTotalAmount: parseFloat(billData.previousTotalAmount) || 0,
        alreadyPaidAmount: alreadyPaidAmount,
        netPayable:
          previousBillAmount +
          presentBillAmount -
          retentionAmount -
          totalPaidAmount,
      });

      // Load final remark
      setFinalRemark(billData.remarks || "");

      // Convert Work Order Items and Additional Items
      const workOrderItemsData = convertApiDataToSubsections(
        billData.workOrderItems || {}
      );
      const additionalItemsData = convertApiDataToSubsections(
        billData.additionalItems || {}
      );

      // Check if we have previous bills and load their data
      const hasPreviousBills = relatedBills && relatedBills.length > 0;
      // When generating a new bill, billData.billId might be undefined
      // so we need to handle this case
      const currentBillIndex = billData?.billId
        ? relatedBills.findIndex((bill) => bill.billId === billData.billId)
        : relatedBills.length;

      // Only process previous bills if this isn't the first bill
      if (hasPreviousBills && currentBillIndex > 0) {
        // Add previous bill rows to each subsection
        workOrderItemsData.forEach((subsection) => {
          // For each subsection, look for matching data in previous bills
          let allPreviousRows = [];
          let billCounter = 1; // Get the current bill index
          // When generating a new bill, billData?.billId will be undefined
          // so we need to set currentBillIndex to the length of relatedBills
          // to include ALL previous bills
          const currentBillIndex = billData?.billId
            ? relatedBills.findIndex((bill) => bill.billId === billData.billId)
            : relatedBills.length;
          // Process bills in reverse order (newest to oldest)
          // // This way oldest bills appear first in the list
          for (let i = currentBillIndex - 1; i >= 0; i--) {
            const prevBill = relatedBills[i];
            if (prevBill && prevBill.workOrderItems) {
              // Find matching subsection in previous bill
              Object.values(prevBill.workOrderItems).forEach(
                (prevSubsection) => {
                  if (
                    prevSubsection.category === subsection.title &&
                    prevSubsection.section === subsection.subtitle
                  ) {
                    // Found matching subsection, process its items
                    if (prevSubsection.items) {
                      // Get first table or create one if needed
                      const targetTable =
                        subsection.tables && subsection.tables.length > 0
                          ? subsection.tables[0]
                          : {
                              id: `table-${generateId()}`,
                              tableTitle: "Measurement Table",
                              rows: [],
                            };

                      // Convert items to rows
                      const rowsFromThisBill = Object.entries(
                        prevSubsection.items
                      ).map(([itemId, itemData], rowIndex) => {
                        return {
                          id: `prev-row-${generateId()}`,
                          sNo: allPreviousRows.length + rowIndex + 1,
                          description: ` ${
                            itemData.description || "Measurement"
                          }`,
                          length: itemData.dimensions?.length || 0,
                          breadth: itemData.dimensions?.breadth || 0,
                          height: itemData.dimensions?.height || 0,
                          units: itemData.units || "Unit",
                          nos: itemData.nos || 0,
                          totalQuantity: itemData.totalQuantity || 0,
                          rate: itemData.rate || 0,
                          gst: itemData.gst || 0,
                          amount: itemData.amount || 0,
                          deductions: itemData.deductions
                            ? Object.entries(itemData.deductions).map(
                                ([deductId, deduct]) => ({
                                  id: `prev-deduct-${generateId()}`,
                                  description: ` ${
                                    deduct.description || "Deduction"
                                  }`,
                                  length: deduct.dimensions?.length || 0,
                                  breadth: deduct.dimensions?.breadth || 0,
                                  height: deduct.dimensions?.height || 0,
                                  units:
                                    deduct.units || itemData.units || "Unit",
                                  nos: deduct.nos || 0,
                                  totalQuantity: deduct.totalQuantity || 0,
                                  rate: deduct.rate || 0,
                                  gst: deduct.gst || 0,
                                  amount: deduct.amount || 0,
                                })
                              )
                            : [],
                          isPrevious: true, // Mark as from previous bill
                          billNumber: billCounter, // Store which bill this came from
                          fromBillId: prevBill.billId, // Store the original bill ID
                        };
                      });

                      // Add previous rows to the collection
                      // // Insert newest bills first so they appear at the bottom
                      allPreviousRows = [
                        ...rowsFromThisBill,
                        ...allPreviousRows,
                      ];

                      // Add to the first table
                      if (subsection.tables && subsection.tables.length > 0) {
                        // Add previous rows to the end of the first table
                        // // This ensures newest bills appear last in the table
                        subsection.tables[0].rows = [
                          ...subsection.tables[0].rows,
                          ...rowsFromThisBill,
                        ];
                      } else {
                        // Create a new table with the rows
                        subsection.tables = [targetTable];
                        targetTable.rows = rowsFromThisBill;
                      }
                    }
                  }
                }
              );
            }
            billCounter++;
          }

          // Re-number all rows sequentially
          if (subsection.tables && subsection.tables.length > 0) {
            subsection.tables.forEach((table) => {
              table.rows = getSortedRowsBySNo(table.rows).map((row, idx) => ({
                ...row,
                sNo: idx + 1,
              }));
            });
          }
        });

        // Do the same for additional items
        additionalItemsData.forEach((subsection) => {
          // Same process for additional items
          let allPreviousRows = [];
          let billCounter = 1;

          // When generating a new bill, use all previous bills
          const additionalBillIndex = billData?.billId
            ? relatedBills.findIndex((bill) => bill.billId === billData.billId)
            : relatedBills.length;

          for (let i = 0; i < additionalBillIndex; i++) {
            const prevBill = relatedBills[i];
            if (prevBill && prevBill.additionalItems) {
              Object.values(prevBill.additionalItems).forEach(
                (prevSubsection) => {
                  if (
                    prevSubsection.category === subsection.title &&
                    prevSubsection.section === subsection.subtitle
                  ) {
                    if (prevSubsection.items) {
                      const targetTable =
                        subsection.tables && subsection.tables.length > 0
                          ? subsection.tables[0]
                          : {
                              id: `table-${generateId()}`,
                              tableTitle: "Measurement Table",
                              rows: [],
                            };

                      const rowsFromThisBill = Object.entries(
                        prevSubsection.items
                      ).map(([itemId, itemData], rowIndex) => {
                        return {
                          id: `prev-row-${generateId()}`,
                          sNo: allPreviousRows.length + rowIndex + 1,
                          description: `${
                            itemData.description || "Measurement"
                          }`,
                          length: itemData.dimensions?.length || 0,
                          breadth: itemData.dimensions?.breadth || 0,
                          height: itemData.dimensions?.height || 0,
                          units: itemData.units || "Unit",
                          nos: itemData.nos || 0,
                          totalQuantity: itemData.totalQuantity || 0,
                          rate: itemData.rate || 0,
                          gst: itemData.gst || 0,
                          amount: itemData.amount || 0,
                          deductions: itemData.deductions
                            ? Object.entries(itemData.deductions).map(
                                ([deductId, deduct]) => ({
                                  id: `prev-deduct-${generateId()}`,
                                  description: `${
                                    deduct.description || "Deduction"
                                  }`,
                                  length: deduct.dimensions?.length || 0,
                                  breadth: deduct.dimensions?.breadth || 0,
                                  height: deduct.dimensions?.height || 0,
                                  units:
                                    deduct.units || itemData.units || "Unit",
                                  nos: deduct.nos || 0,
                                  totalQuantity: deduct.totalQuantity || 0,
                                  rate: deduct.rate || 0,
                                  gst: deduct.gst || 0,
                                  amount: deduct.amount || 0,
                                })
                              )
                            : [],
                          isPrevious: true,
                          billNumber: billCounter,
                          fromBillId: prevBill.billId,
                        };
                      });

                      allPreviousRows = [
                        ...allPreviousRows,
                        ...rowsFromThisBill,
                      ];

                      if (subsection.tables && subsection.tables.length > 0) {
                        // Add previous rows to the end of the first table
                        // // This ensures newest bills appear last in the table
                        subsection.tables[0].rows = [
                          ...subsection.tables[0].rows,
                          ...rowsFromThisBill,
                        ];
                      } else {
                        subsection.tables = [targetTable];
                        targetTable.rows = rowsFromThisBill;
                      }
                    }
                  }
                }
              );
            }
            billCounter++;
          }

          // Re-number all rows sequentially
          if (subsection.tables && subsection.tables.length > 0) {
            subsection.tables.forEach((table) => {
              table.rows = getSortedRowsBySNo(table.rows).map((row, idx) => ({
                ...row,
                sNo: idx + 1,
              }));
            });
          }
        });
      }

      // Sort all rows by sNo
      const sortAllRowsBySNo = (subsections) => {
        return subsections.map((subsection) => {
          if (subsection.tables) {
            const updatedTables = subsection.tables.map((table) => {
              return {
                ...table,
                rows: getSortedRowsBySNo(table.rows),
              };
            });
            return {
              ...subsection,
              tables: updatedTables,
            };
          }
          return subsection;
        });
      };

      // Update sections with sorted data
      setSections([
        {
          id: "workOrderItems",
          title: "Work Order Items",
          isExpanded: true,
          subsections: sortAllRowsBySNo(workOrderItemsData),
        },
        {
          id: "additionalItems",
          title: "Additional Items",
          isExpanded: true,
          subsections: sortAllRowsBySNo(additionalItemsData),
        },
      ]);

      console.log("Successfully loaded bill data");
      return true;
    } catch (error) {
      console.error("Error loading from existing bill:", error);
      toast.error("Error loading bill data. Using default values.");
      initializeWithSampleData();
      return false;
    }
  };

  // Convert API data format to our subsections format
  const convertApiDataToSubsections = (apiData) => {
    const subsections = [];

    Object.entries(apiData).forEach(([subsectionId, subsectionData]) => {
      // Initialize tables map with stored table data or create a default one
      const tableMap = new Map();

      // If we have tables data from the API, use it
      if (
        subsectionData.tables &&
        Object.keys(subsectionData.tables).length > 0
      ) {
        Object.entries(subsectionData.tables).forEach(
          ([tableId, tableData]) => {
            tableMap.set(tableId, {
              id: tableId,
              tableTitle: tableData.title || "Measurement Table",
              rows: [],
            });
          }
        );
      } else {
        // Create one default table if none exists
        const defaultTableId = `table-${generateId()}`;
        tableMap.set(defaultTableId, {
          id: defaultTableId,
          tableTitle: "Measurement Table",
          rows: [],
        });
      }

      // Process items into tables
      if (subsectionData.items) {
        Object.entries(subsectionData.items).forEach(([itemId, itemData]) => {
          // Determine which table this item belongs to
          let targetTableId;

          if (itemData.tableId && tableMap.has(itemData.tableId)) {
            // Use the stored tableId if it exists
            targetTableId = itemData.tableId;
          } else {
            // If no tableId or it doesn't match any known table, use the first table
            targetTableId = tableMap.keys().next().value;
          }

          // Create row from item
          const row = {
            id: itemId,
            sNo: itemData.index || tableMap.get(targetTableId).rows.length + 1, // Use index from API data
            description: itemData.description || "",
            length: itemData.dimensions?.length || 0,
            breadth: itemData.dimensions?.breadth || 0,
            height: itemData.dimensions?.height || 0,
            units: itemData.units || "Unit",
            nos: itemData.nos || 0,
            totalQuantity: itemData.totalQuantity || 0,
            rate: itemData.rate || 0,
            gst: itemData.gst || 0,
            amount: itemData.amount || 0,
            deductions: [],
          };

          // Add deductions if any
          if (itemData.deductions) {
            Object.entries(itemData.deductions).forEach(
              ([deductionId, deductionData]) => {
                row.deductions.push({
                  id: deductionId,
                  description: deductionData.description || "Deduction",
                  length: deductionData.dimensions?.length || 0,
                  breadth: deductionData.dimensions?.breadth || 0,
                  height: deductionData.dimensions?.height || 0,
                  units: deductionData.units || row.units,
                  nos: deductionData.nos || 0,
                  totalQuantity: deductionData.totalQuantity || 0,
                  rate: deductionData.rate || row.rate,
                  amount: deductionData.amount || 0,
                  gst: deductionData.gst || 0,
                });
              }
            );
          }

          // Add row to the appropriate table
          tableMap.get(targetTableId).rows.push(row);
        });
      }

      // Sort rows in each table by sNo
      tableMap.forEach((table) => {
        table.rows.sort((a, b) => a.sNo - b.sNo);
      });

      // Convert tableMap to array
      const tables = Array.from(tableMap.values());

      // Create subsection
      subsections.push({
        id: subsectionId,
        title: subsectionData.category || "Category", // Category should be the title
        subtitle: subsectionData.section || "Description", // Section should be the subtitle (description)
        poQuantity: parseFloat(subsectionData.poQuantity) || 0,
        totalClaimedQuantity: parseFloat(subsectionData.claimedQuantity) || 0,
        totalApprovedQuantity: parseFloat(subsectionData.approvedQuantity) || 0,
        units: subsectionData.unit || "Unit",
        totalAmount: calculateSubsectionTotal(tables),
        isExpanded: true,
        tables: tables,
      });
    });

    return subsections;
  };

  // Calculate the total amount for a subsection based on its tables
  const calculateSubsectionTotal = (tables) => {
    let total = 0;
    let previousTotal = 0;
    let presentTotal = 0;

    tables.forEach((table) => {
      table.rows.forEach((row) => {
        const rowAmount = parseFloat(row.amount) || 0;
        total += rowAmount;

        // Track previous vs present amounts separately
        if (row.isPrevious) {
          previousTotal += rowAmount;
        } else {
          presentTotal += rowAmount;
        }

        if (row.deductions) {
          let rowDeductions = 0;
          row.deductions.forEach((deduction) => {
            const deductAmount = parseFloat(deduction.amount) || 0;
            total -= deductAmount;
            rowDeductions += deductAmount;
          });

          // Deduct from the appropriate total
          if (row.isPrevious) {
            previousTotal -= rowDeductions;
          } else {
            presentTotal -= rowDeductions;
          }
        }
      });
    });

    // For compatibility, return the total amount
    // The presentTotal will be used when saving to the database
    return total;
  };

  // Create subsections from the selected line items in the PO
  const createSubsectionsFromSelectedItems = (items, referenceBill = null) => {
    try {
      const updatedSections = [...sections];
      const workOrderSection = updatedSections.find(
        (s) => s.id === "workOrderItems"
      );
      let subsections = [];

      // Check if we have previous bills
      const hasPreviousBills = relatedBills && relatedBills.length > 0;

      // Log the reference bill if available
      if (referenceBill) {
        console.log(
          "Using reference bill for item matching:",
          referenceBill.billId
        );
      }

      if (workOrderSection) {
        // Create a subsection for each selected item
        subsections = items.map((item) => {
          const subsectionId = `subsection-${generateId()}`;
          const tableId = `table-${generateId()}`;

          // Create initial rows for the table
          let initialRows = [];
          let hasAddedPreviousItems = false;

          // Check for all previous bills if available
          if (hasPreviousBills) {
            // If we have relatedBills, process them all to show history
            const allBills = relatedBills.slice().sort((a, b) => {
              // Sort by timestamp/billId to ensure chronological order
              return parseInt(a.billId) - parseInt(b.billId);
            });

            // Add a header row for previous bills section

            // Process each bill to find matching items
            allBills.forEach((prevBill, billIndex) => {
              if (prevBill.workOrderItems) {
                // Look for matching heading/category in previous bills
                Object.entries(prevBill.workOrderItems).forEach(
                  ([subsectionId, subsection]) => {
                    // Check if this subsection matches our current item
                    if (
                      subsection.category === (item.heading || item.category) &&
                      subsection.section ===
                        (item.specification || item.description)
                    ) {
                      // Found matching subsection, add a bill identifier row

                      // Add all items from this subsection
                      if (subsection.items) {
                        Object.entries(subsection.items).forEach(
                          ([itemId, itemData], itemIndex) => {
                            // Add the item row
                            initialRows.push({
                              id: `prev-row-${generateId()}`,
                              sNo: initialRows.length + 1,
                              description:
                                itemData.description ||
                                `Measurement ${itemIndex + 1}`,
                              length: itemData.dimensions?.length || 0,
                              breadth: itemData.dimensions?.breadth || 0,
                              height: itemData.dimensions?.height || 0,
                              units: itemData.units || item.unit || "Unit",
                              nos: itemData.nos || 0,
                              totalQuantity: itemData.totalQuantity || 0,
                              rate: itemData.rate || parseFloat(item.rate) || 0,
                              amount: itemData.amount || 0,
                              deductions: itemData.deductions
                                ? Object.entries(itemData.deductions).map(
                                    ([deductId, deduct]) => ({
                                      id: `prev-deduct-${generateId()}`,
                                      description:
                                        deduct.description || "Deduction",
                                      length: deduct.dimensions?.length || 0,
                                      breadth: deduct.dimensions?.breadth || 0,
                                      height: deduct.dimensions?.height || 0,
                                      units:
                                        deduct.units ||
                                        itemData.units ||
                                        item.unit ||
                                        "Unit",
                                      nos: deduct.nos || 0,
                                      totalQuantity: deduct.totalQuantity || 0,
                                      rate: deduct.rate || 0,
                                      amount: deduct.amount || 0,
                                    })
                                  )
                                : [],
                              isPrevious: true,
                              billNumber: billIndex + 1,
                              fromBillId: prevBill.billId,
                            });
                            hasAddedPreviousItems = true;
                          }
                        );
                      }
                    }
                  }
                );
              }
            });

            // Only add the divider if we actually added previous items
            // if (hasAddedPreviousItems) {
            //   // Add a divider row between previous and current items
            //   initialRows.push({
            //     id: `divider-end-${generateId()}`,
            //     sNo: initialRows.length + 1,
            //     description: "--- Current Bill Items ---",
            //     length: 0,
            //     breadth: 0,
            //     height: 0,
            //     units: item.unit || "Unit",
            //     nos: 0,
            //     totalQuantity: 0,
            //     rate: 0,
            //     amount: 0,
            //     deductions: [],
            //     isPrevious: false,
            //     isHeader: true,
            //   });
            // } else {
            //   // If no previous items were found, remove the header
            //   initialRows = initialRows.filter((row) => !row.isHeader);
            // }
          }

          // Add default empty row(s) for new entries
          // Always add at least one row for new data
          initialRows.push({
            id: `row-${generateId()}`,
            sNo: initialRows.length + 1,
            description:
              item.specification || item.description || "New measurement",
            length: 0,
            breadth: 0,
            height: 0,
            units: item.unit || "Unit",
            nos: 0,
            totalQuantity: 0,
            rate: parseFloat(item.rate) || 0,
            amount: 0,
            deductions: [],
          });

          // Add one more empty row
          initialRows.push({
            id: `row-${generateId()}`,
            sNo: initialRows.length + 1,
            description: "",
            length: 0,
            breadth: 0,
            height: 0,
            units: item.unit || "Unit",
            nos: 0,
            totalQuantity: 0,
            rate: parseFloat(item.rate) || 0,
            amount: 0,
            deductions: [],
          });

          // Calculate total amount (excluding headers and previous items for this calculation)
          const totalAmount = initialRows
            .filter((row) => !row.isHeader && !row.isSubheader)
            .reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);

          return {
            id: subsectionId,
            title: item.heading || item.category || "Work Item", // Use heading as category (title)
            subtitle: item.specification || item.description || "Description", // Use specification/description as description (subtitle)
            poQuantity: parseFloat(item.quantity) || 0,
            totalClaimedQuantity: parseFloat(item.quantity) || 0,
            totalApprovedQuantity: 0,
            units: item.unit || "Unit",
            totalAmount: totalAmount,
            isExpanded: true,
            tables: [
              {
                id: tableId,
                tableTitle: "Measurement Table",
                rows: initialRows,
              },
            ],
          };
        });

        workOrderSection.subsections = subsections;
      }

      setSections(updatedSections);

      // Calculate total amount for bill summary
      const totalPresent = subsections.reduce(
        (sum, subsection) => sum + (parseFloat(subsection.totalAmount) || 0),
        0
      );

      // Calculate previous bill amount
      let previousBillAmount = 0;
      if (relatedBills && relatedBills.length > 0) {
        // Sum up amounts from all previous bills
        relatedBills.forEach((bill) => {
          previousBillAmount += parseFloat(bill.totalAmount || 0);
        });
      }

      setBillSummary((prev) => {
        const retentionMoney = (totalPresent * prev.retentionPercentage) / 100;
        return {
          ...prev,
          previousBill: previousBillAmount,
          presentBill: totalPresent,
          totalAmount: previousBillAmount + totalPresent,
          retentionMoney,
          netPayable: totalPresent - retentionMoney,
        };
      });

      console.log("Created subsections from selected items");
    } catch (error) {
      console.error("Error creating subsections from selected items:", error);
      toast.error("Error processing selected items. Using default values.");
      initializeWithSampleData();
    }
  };

  // Initialize with sample data if needed
  const initializeWithSampleData = () => {
    // Sample data for workOrderItems section
    const sampleWorkOrderSubsection = {
      id: "subsection-1",
      title: "subsection-1",
      subtitle: "Enter the work order items",
      poQuantity: 0,
      totalClaimedQuantity: 0,
      totalApprovedQuantity: 0,
      units: "Unit",
      totalAmount: 0,
      isExpanded: true,
      tables: [
        {
          id: "table-1",
          tableTitle: "Measurement Table",
          rows: Array(5)
            .fill()
            .map((_, index) => ({
              id: `row-${index}`,
              sNo: index + 1,
              description: index === 0 ? "Enter the description here" : "",
              length: index === 0 ? 0 : 0,
              breadth: index === 0 ? 0 : 0,
              height: index === 0 ? 0 : 0,
              units: "Unit",
              nos: index === 0 ? 0 : 0,
              totalQuantity: index === 0 ? 0 : 0,
              rate: index === 0 ? 0 : 0,
              amount: index === 0 ? 0 : 0,
              deductions: [],
            })),
        },
      ],
    };

    // Sample data for additionalItems section
    const sampleAdditionalSubsection = {
      id: "subsection-2",
      title: "Enter the additional items",
      subtitle: "Additional Items",
      poQuantity: 0,
      totalClaimedQuantity: 0,
      totalApprovedQuantity: 0,
      units: "Unit",
      totalAmount: 0,
      isExpanded: true,
      tables: [
        {
          id: "table-2",
          tableTitle: "Enter the additional items",
          rows: Array(5)
            .fill()
            .map((_, index) => ({
              id: `row-${index + 5}`,
              sNo: index + 1,
              description: index === 0 ? "" : "",
              length: index === 0 ? 0 : 0,
              breadth: index === 0 ? 0 : 0,
              height: index === 0 ? 0 : 0,
              units: "",
              nos: index === 0 ? 1 : 0,
              totalQuantity: index === 0 ? 0 : 0,
              rate: index === 0 ? 0 : 0,
              amount: index === 0 ? 0 : 0,
              deductions: [],
            })),
        },
      ],
    };

    // Update sections with sample data
    setSections((prev) =>
      prev.map((section) => {
        if (section.id === "workOrderItems") {
          return {
            ...section,
            subsections: [sampleWorkOrderSubsection],
          };
        } else if (section.id === "additionalItems") {
          return {
            ...section,
            subsections: [sampleAdditionalSubsection],
          };
        }
        return section;
      })
    );

    // Calculate previous bill amount from related bills if available
    let previousBillAmount = 0; // If we have related bills and this isn't the first bill, get amount from previous bill
    if (relatedBills && relatedBills.length > 0 && currentBillIndex > 0) {
      previousBillAmount =
        parseFloat(relatedBills[currentBillIndex - 1].totalAmount) || 0;
    }

    // Calculate present bill amount (total of both sections)
    const presentBillAmount = 0; // 5000 + 10000 from the two sample subsections

    // Calculate total paid amount from previous bills
    let totalPaidAmount = 0;
    if (relatedBills && relatedBills.length > 0 && currentBillIndex > 0) {
      // Sum up paid amounts from all previous bills
      for (let i = 0; i < currentBillIndex; i++) {
        const bill = relatedBills[i];
        if (bill && bill.status === "Paid") {
          totalPaidAmount += parseFloat(
            bill.paidAmount || bill.totalAmount || 0
          );
        }
      }
    }

    // Update bill summary with correct values
    setBillSummary({
      previousBill: previousBillAmount,
      presentBill: presentBillAmount,
      totalAmount: previousBillAmount + presentBillAmount,
      retentionPercentage: 0,
      retentionMoney: (presentBillAmount * 0) / 100,
      previousTotalAmount: previousBillAmount,
      netPayable:
        previousBillAmount +
        presentBillAmount -
        (presentBillAmount * 0) / 100 -
        totalPaidAmount,
    });
  };

  // Add a ref to track if the sections are being updated
  const isUpdatingSections = useRef(false);

  // Update total amounts whenever section data changes - fix infinite update loop
  useEffect(() => {
    // If we're already updating, don't trigger another update
    if (isUpdatingSections.current) {
      return;
    }

    isUpdatingSections.current = true;

    try {
      // Check if we actually need to update
      let needsUpdate = false;
      const updatedSections = sections.map((section) => {
        if (section.subsections) {
          const updatedSubsections = section.subsections.map((subsection) => {
            let subsectionTotal = 0;
            let subsectionDeductions = 0;

            if (subsection.tables) {
              subsection.tables.forEach((table) => {
                table.rows.forEach((row) => {
                  subsectionTotal += parseFloat(row.amount) || 0;

                  if (row.deductions && row.deductions.length > 0) {
                    row.deductions.forEach((deduction) => {
                      subsectionDeductions += parseFloat(deduction.amount) || 0;
                    });
                  }
                });
              });
            }

            const newTotal = subsectionTotal - subsectionDeductions;
            if (newTotal !== subsection.totalAmount) {
              needsUpdate = true;
              return {
                ...subsection,
                totalAmount: newTotal,
              };
            }
            return subsection;
          });

          if (needsUpdate) {
            return {
              ...section,
              subsections: updatedSubsections,
            };
          }
        }
        return section;
      });

      // Only update if something changed
      if (needsUpdate) {
        setSections(updatedSections);
      }

      // Calculate bill summary - do this regardless of section changes
      // to ensure present bill is always correctly calculated
      const totalPresent = calculateTotalAmount(); // Update billSummary with correct calculations
      setBillSummary((prev) => {
        const presentBill = totalPresent;
        const previousBill = prev.previousBill || 0;
        const totalAmount = presentBill + previousBill;
        const retentionPercentage = prev.retentionPercentage || 0;
        // Retention money is calculated as a percentage of the present bill only
        const retentionMoney = (presentBill * retentionPercentage) / 100;
        const alreadyPaidAmount = paidAmount || 0;

        // Net payable is the total amount (previous + present) minus retention money minus already paid amount
        const netPayable = totalAmount - retentionMoney - totalPaidAmount;

        return {
          previousBill: previousBill,
          presentBill: presentBill,
          totalAmount: totalAmount,
          retentionPercentage: retentionPercentage,
          retentionMoney: retentionMoney,
          previousTotalAmount: prev.previousTotalAmount || 0,
          netPayable: netPayable,
          billedAmount: existingBillData.billedAmount || presentBill,
        };
      });
    } finally {
      // Reset the flag regardless of outcome
      isUpdatingSections.current = false;
    }
  }, [sections]);

  // Helper function to format current date as DD-MM-YYYY
  function formatCurrentDate() {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = now.getFullYear();
    return `${day}-${month}-${year}`;
  }

  // Helper function to generate unique ID
  function generateId() {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  // Generate a bill number using timestamp for sequential ordering
  function generateBillNumber() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    // Format: BILL-YYYYMMDDHHmmss (e.g., BILL-20230615142230)
    return `BILL-${new Date().getTime()}`;
  } // Calculate total amount for all sections
  function calculateTotalAmount() {
    let total = 0;

    // Calculate the total by summing up the presentTotal from all tables
    sections.forEach((section) => {
      if (section.subsections) {
        section.subsections.forEach((subsection) => {
          if (subsection.tables) {
            subsection.tables.forEach((table) => {
              // Calculate presentTotal for this table
              const tableTotal = calculateTableTotals(
                section.id,
                subsection.id,
                table.id
              ).presentTotal;

              // Parse as float and add to total, default to 0 if NaN
              total += parseFloat(tableTotal) || 0;
            });
          }
        });
      }
    });

    // Round to 2 decimal places to avoid floating point issues
    total = parseFloat(total.toFixed(2));

    // Update the bill summary with the calculated present bill amount
    setBillSummary((prev) => {
      const previousBill = parseFloat(prev.previousBill) || 0;
      const totalAmount = previousBill + total;
      const retentionPercentage = parseFloat(prev.retentionPercentage) || 0;
      const retentionMoney = (total * retentionPercentage) / 100;
      const alreadyPaidAmount = parseFloat(prev.alreadyPaidAmount) || 0;
      const netPayable = totalAmount - retentionMoney - totalPaidAmount;

      return {
        ...prev,
        presentBill: total,
        totalAmount: totalAmount,
        retentionMoney: retentionMoney,
        netPayable: netPayable,
      };
    });

    // Also update billedAmount to match the present bill
    setBilledAmount(total);

    return total;
  }

  function formatCurrency(amount) {
    if (!amount) return "0.00";
    return parseFloat(amount)
      .toFixed(2)
      .replace(/\d(?=(\d{3})+\.)/g, "$&,");
  }

  function formatCurrencyCompact(amount) {
    if (!amount) return "0";
    const num = parseFloat(amount);

    if (num >= 10000000) {
      // 1 crore+
      return `${(num / 10000000).toFixed(2)} Cr`;
    } else if (num >= 100000) {
      // 1 lakh+
      return `${(num / 100000).toFixed(2)} L`;
    } else if (num >= 1000) {
      // 1000+
      return `${(num / 1000).toFixed(1)}K`;
    } else {
      return num.toFixed(0);
    }
  }
  // Handle selecting a different bill
  const handleBillSelection = (index) => {
    if (index >= 0 && index < relatedBills.length) {
      // Update current bill index first
      setCurrentBillIndex(index);

      // Recalculate already paid amount based on the new bill selection
      setTimeout(() => {
        updatePreviousBillAmount();
      }, 0);

      // Set the current bill data to the component state
      const selectedBill = relatedBills[index];

      // Navigate only if the bill ID is different than the current one
      // Navigate to the selected bill
      loadFromExistingBill(selectedBill);
      history.push("/RaiseBill", {
        data: {
          ProjectId: projectId,
          ProjectName: projectName,
          viewMode: true,
          billId: selectedBill.billId,
          billData: selectedBill,
          poId: selectedBill.poId,
          readOnly: selectedBill.status === "Approved",
          relatedBills: relatedBills, // Pass all related bills again
          currentBillIndex: index, // Pass the current index
          vendor: selectedBill.vendor,
          client: selectedBill.client,
          serviceOrder: selectedBill,
        },
      });
    }
  };

  const toggleSection = (sectionId) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId
          ? { ...section, isExpanded: !section.isExpanded }
          : section
      )
    );
  };

  // Toggle subsection expansion
  const toggleSubsection = (sectionId, subsectionId) => {
    setSections(
      sections.map((section) => {
        if (section.id === sectionId && section.subsections) {
          return {
            ...section,
            subsections: section.subsections.map((subsection) =>
              subsection.id === subsectionId
                ? { ...subsection, isExpanded: !subsection.isExpanded }
                : subsection
            ),
          };
        }
        return section;
      })
    );
  };

  // Ensure rows are sorted by billNumber first (ascending), then by serial number (sNo)
  const getSortedRowsBySNo = (rows) => {
    // Create a shallow copy to avoid modifying the original array
    return [...rows].sort((a, b) => {
      // First sort by billNumber (if present) to group by bill
      // This ensures older bills (lower bill numbers) appear first
      if (a.billNumber && b.billNumber) {
        if (a.billNumber !== b.billNumber) {
          return a.billNumber - b.billNumber;
        }
      } else if (a.billNumber) {
        return -1; // Previous bill items (with billNumber) come first
      } else if (b.billNumber) {
        return 1; // Current bill items (without billNumber) come last
      }

      // Then sort by sNo within each bill group
      const aNo = parseInt(a.sNo, 10) || 0;
      const bNo = parseInt(b.sNo, 10) || 0;
      return aNo - bNo;
    });
  };

  // Handle field editing - only if not readOnly
  const handleFieldEdit = (
    field,
    value,
    section = null,
    subsection = null,
    tableId = null,
    rowId = null,
    deductionId = null
  ) => {
    // Mark that changes have been made if in edit mode for approved bill
    if (isEditingApprovedBill && billStatus === "Approved") {
      setHasChanges(true);
    }

    if (readOnly) return; // Don't allow edits in readOnly mode

    // Handle special fields
    if (field === "billNo") {
      setBillNumber(value);
      return;
    }

    if (field === "billType") {
      setBillType(value);
      return;
    }

    if (field === "billDate") {
      setBillDate(value);
      return;
    }

    if (field === "finalRemark") {
      setFinalRemark(value);
      return;
    }

    if (field === "billedAmount") {
      setBilledAmount(value);
      return;
    }

    // Handle client details fields
    if (field.startsWith("client.")) {
      const clientField = field.split(".")[1];
      setClientDetails((prev) => ({
        ...prev,
        [clientField]: value,
      }));
      return;
    }

    // Handle vendor details fields
    if (field.startsWith("vendor.")) {
      const vendorField = field.split(".")[1];
      setVendorDetails((prev) => ({
        ...prev,
        [vendorField]: value,
      }));
      return;
    }

    // Handle person details fields
    if (field.startsWith("person.")) {
      const personField = field.split(".")[1];
      setPersonDetails((prev) => ({
        ...prev,
        [personField]: value,
      }));
      return;
    }

    // Handle payment details fields
    if (field.startsWith("payment.")) {
      const paymentField = field.split(".")[1];
      setPaymentDetails((prev) => ({
        ...prev,
        [paymentField]: value,
      }));
      return;
    }

    // Handle approval details fields
    if (field.startsWith("approval.")) {
      const approvalField = field.split(".")[1];
      setApprovalDetails((prev) => ({
        ...prev,
        [approvalField]: value,
      }));
      return;
    }

    // Handle bill summary fields
    if (field.startsWith("summary.")) {
      const summaryField = field.split(".")[1];
      setBillSummary((prev) => {
        const updatedSummary = {
          ...prev,
          [summaryField]: value,
        };

        // If retention percentage is changed, recalculate retention money
        if (summaryField === "retentionPercentage") {
          updatedSummary.retentionMoney =
            (updatedSummary.presentBill * parseFloat(value)) / 100; //update net payable based on total amount, not just present bill
          updatedSummary.netPayable =
            updatedSummary.totalAmount -
            updatedSummary.retentionMoney -
            (totalPaidAmount || 0);
        } // If present bill is changed, recalculate total amount and retention money
        if (summaryField === "presentBill") {
          const presentBillValue = parseFloat(value) || 0;

          // Also update billedAmount to keep them in sync
          setBilledAmount(presentBillValue);

          updatedSummary.totalAmount =
            presentBillValue + (prev.previousBill || 0);
          updatedSummary.retentionMoney =
            (presentBillValue * prev.retentionPercentage) / 100;

          // Also update net payable - correctly calculate as total amount minus retention minus already paid
          updatedSummary.netPayable =
            updatedSummary.totalAmount -
            updatedSummary.retentionMoney -
            (totalPaidAmount || 0);
        } // If previous bill is changed, recalculate total amount
        if (summaryField === "previousBill") {
          updatedSummary.totalAmount =
            (prev.presentBill || billedAmount || 0) + parseFloat(value);

          // Previous bill doesn't affect retention money or net payable
          // as those are calculated from the present bill amount
          updatedSummary.previousTotalAmount = parseFloat(value);
        } // If already paid amount is changed, recalculate net payable
        if (
          summaryField === "alreadyPaidAmount" ||
          summaryField === "totalAmount" ||
          summaryField === "retentionMoney"
        ) {
          // Ensure we're calculating net payable properly as total - retention - already paid
          updatedSummary.netPayable =
            updatedSummary.totalAmount -
            updatedSummary.retentionMoney -
            updatedSummary;
        }

        return updatedSummary;
      });
      return;
    }

    // Handle section, subsection, and row fields
    if (section && subsection) {
      setSections((prev) =>
        prev.map((s) => {
          if (s.id !== section) return s;

          return {
            ...s,
            subsections: s.subsections?.map((sub) => {
              if (sub.id !== subsection) return sub;

              // Handle tableTitle field
              if (field === "tableTitle" && tableId) {
                return {
                  ...sub,
                  tables: sub.tables?.map((table) => {
                    if (table.id !== tableId) return table;
                    return {
                      ...table,
                      tableTitle: value,
                    };
                  }),
                };
              }

              // Handle subsection title/subtitle
              if (
                field === "title" ||
                field === "subtitle" ||
                field === "units" ||
                field === "poQuantity"
              ) {
                return {
                  ...sub,
                  [field]: value,
                };
              }

              // Handle row fields
              if (tableId && rowId) {
                return {
                  ...sub,
                  tables: sub.tables?.map((table) => {
                    if (table.id !== tableId) return table;

                    if (deductionId) {
                      // Handle deduction field edits
                      return {
                        ...table,
                        rows: table.rows.map((row) => {
                          if (row.id !== rowId) return row;

                          return {
                            ...row,
                            deductions: row.deductions.map((deduction) => {
                              if (deduction.id !== deductionId)
                                return deduction;

                              // Update the deduction field
                              const updatedDeduction = {
                                ...deduction,
                                [field]: value,
                              };

                              // Handle numeric fields and calculate derived values
                              if (
                                field === "length" ||
                                field === "breadth" ||
                                field === "height" ||
                                field === "nos"
                              ) {
                                const length = parseFloat(
                                  field === "length"
                                    ? value
                                    : updatedDeduction.length || 0
                                );
                                const breadth = parseFloat(
                                  field === "breadth"
                                    ? value
                                    : updatedDeduction.breadth || 0
                                );
                                const height = parseFloat(
                                  field === "height"
                                    ? value
                                    : updatedDeduction.height || 0
                                );
                                const nos = parseFloat(
                                  field === "nos"
                                    ? value
                                    : updatedDeduction.nos || 0
                                );

                                // Calculate total quantity
                                const totalQuantity = parseFloat(
                                  (length * breadth * height * nos).toFixed(2)
                                );
                                updatedDeduction.totalQuantity = totalQuantity;

                                // Calculate amount
                                const rate = parseFloat(
                                  updatedDeduction.rate || 0
                                );
                                updatedDeduction.amount = parseFloat(
                                  (totalQuantity * rate).toFixed(2)
                                );
                              }

                              // Handle rate updates
                              if (field === "rate") {
                                const totalQuantity = parseFloat(
                                  updatedDeduction.totalQuantity || 0
                                );
                                const rate = parseFloat(value || 0);
                                updatedDeduction.amount = parseFloat(
                                  (totalQuantity * rate).toFixed(2)
                                );
                              }

                              // Handle gst updates
                              if (field === "gst") {
                                const totalQuantity = parseFloat(
                                  updatedDeduction.totalQuantity || 0
                                );
                                const rate = parseFloat(
                                  updatedDeduction.rate || 0
                                );
                                updatedDeduction.amount = parseFloat(
                                  (totalQuantity * rate).toFixed(2)
                                );
                                updatedDeduction.gst = parseFloat(value || 0);
                                updatedDeduction.amount = parseFloat(
                                  (
                                    updatedDeduction.amount *
                                    (1 + updatedDeduction.gst / 100)
                                  ).toFixed(2)
                                );
                              }

                              // Handle direct totalQuantity updates
                              if (field === "totalQuantity") {
                                const totalQuantity = parseFloat(value || 0);
                                const rate = parseFloat(
                                  updatedDeduction.rate || 0
                                );
                                updatedDeduction.amount = parseFloat(
                                  (totalQuantity * rate).toFixed(2)
                                );
                              }

                              // Handle direct amount updates
                              if (field === "amount") {
                                // Just update the amount directly
                                updatedDeduction.amount = parseFloat(
                                  value || 0
                                );
                              }

                              return updatedDeduction;
                            }),
                          };
                        }),
                      };
                    } else {
                      // Handle regular row field edits
                      return {
                        ...table,
                        rows: table.rows.map((row) => {
                          if (row.id !== rowId) return row;

                          // Update the field
                          const updatedRow = {
                            ...row,
                            [field]: value,
                          };

                          // Handle numeric fields and calculate derived values
                          if (
                            field === "length" ||
                            field === "breadth" ||
                            field === "height" ||
                            field === "nos"
                          ) {
                            const length = parseFloat(
                              field === "length"
                                ? value
                                : updatedRow.length || 0
                            );
                            const breadth = parseFloat(
                              field === "breadth"
                                ? value
                                : updatedRow.breadth || 0
                            );
                            const height = parseFloat(
                              field === "height"
                                ? value
                                : updatedRow.height || 0
                            );
                            const nos = parseFloat(
                              field === "nos" ? value : updatedRow.nos || 0
                            );

                            // Calculate total quantity
                            const totalQuantity = parseFloat(
                              (length * breadth * height * nos).toFixed(2)
                            );
                            updatedRow.totalQuantity = totalQuantity;

                            // Calculate amount
                            const rate = parseFloat(updatedRow.rate || 0);
                            updatedRow.amount = parseFloat(
                              (totalQuantity * rate).toFixed(2)
                            );
                          }

                          // Handle rate updates
                          if (field === "gst") {
                            const totalQuantity = parseFloat(
                              updatedRow.totalQuantity || 0
                            );
                            const rate = parseFloat(updatedRow.rate || 0);
                            updatedRow.amount = parseFloat(
                              (totalQuantity * rate).toFixed(2)
                            );
                            updatedRow.gst = parseFloat(value || 0);
                            updatedRow.amount = parseFloat(
                              (
                                updatedRow.amount *
                                (1 + updatedRow.gst / 100)
                              ).toFixed(2)
                            );
                          }

                          if (field === "rate") {
                            const totalQuantity = parseFloat(
                              updatedRow.totalQuantity || 0
                            );
                            const rate = parseFloat(value || 0);
                            updatedRow.amount = parseFloat(
                              (totalQuantity * rate).toFixed(2)
                            );
                          }

                          // Handle direct totalQuantity updates
                          if (field === "totalQuantity") {
                            const totalQuantity = parseFloat(value || 0);
                            const rate = parseFloat(updatedRow.rate || 0);

                            updatedRow.amount = parseFloat(
                              (totalQuantity * rate).toFixed(2)
                            );
                          }

                          // Handle direct amount updates
                          if (field === "amount") {
                            // Just update the amount directly
                            updatedRow.amount = parseFloat(value || 0);
                          }

                          return updatedRow;
                        }),
                      };
                    }
                  }),
                };
              }

              return sub;
            }),
          };
        })
      );
    }
  };

  // Add a new row to a table
  const addRow = (sectionId, subsectionId, tableId) => {
    if (readOnly) return; // Don't allow adding rows in readOnly mode

    setSections((prev) =>
      prev.map((section) => {
        if (section.id === sectionId && section.subsections) {
          return {
            ...section,
            subsections: section.subsections.map((subsection) => {
              if (subsection.id === subsectionId && subsection.tables) {
                return {
                  ...subsection,
                  tables: subsection.tables.map((table) => {
                    if (table.id === tableId) {
                      // Sort existing rows to ensure proper sNo values
                      const sortedRows = getSortedRowsBySNo(table.rows);

                      const newRow = {
                        id: `row-${generateId()}`,
                        sNo: sortedRows.length + 1,
                        description: "",
                        length: 0,
                        breadth: 0,
                        height: 0,
                        units: subsection.units || "Unit",
                        nos: 0,
                        totalQuantity: 0,
                        rate: 0,
                        gst: 0,
                        amount: 0,
                        deductions: [],
                      };

                      // Add 3 more rows automatically if there are only 2 rows left
                      // after adding this new one (total will be 3 remaining)
                      if (sortedRows.length === 1) {
                        const additionalRows = Array(3)
                          .fill()
                          .map((_, index) => {
                            return {
                              id: `row-${generateId()}`,
                              sNo: sortedRows.length + 2 + index,
                              description: "",
                              length: 0,
                              breadth: 0,
                              height: 0,
                              units: subsection.units || "Unit",
                              nos: 0,
                              totalQuantity: 0,
                              rate: 0,
                              amount: 0,
                              gst: 0,
                              deductions: [],
                            };
                          });

                        return {
                          ...table,
                          rows: [...table.rows, newRow, ...additionalRows],
                        };
                      }

                      return {
                        ...table,
                        rows: [...table.rows, newRow],
                      };
                    }
                    return table;
                  }),
                };
              }
              return subsection;
            }),
          };
        }
        return section;
      })
    );
  };

  // Add a new subsection to a section
  const addSubsection = (sectionId) => {
    if (readOnly) return; // Don't allow adding subsections in readOnly mode

    const subsectionId = `subsection-${generateId()}`;
    const tableId = `table-${generateId()}`;

    // Create initial rows for the table
    const initialRows = Array(5)
      .fill()
      .map((_, index) => {
        return {
          id: `row-${generateId()}`,
          sNo: index + 1,
          description: index === 0 ? "New Work Item" : "",
          length: 0,
          breadth: 0,
          height: 0,
          units: "Unit",
          nos: 0,
          totalQuantity: 0,
          rate: 0,
          amount: 0,
          gst: 0,
          deductions: [],
        };
      });

    // Create new subsection
    const newSubsection = {
      id: subsectionId,
      title: `${
        sectionId === "workOrderItems" ? "New" : "Additional"
      } Category ${generateId().slice(0, 4)}`,
      subtitle: "Work Item Description",
      poQuantity: 0,
      totalClaimedQuantity: 0,
      totalApprovedQuantity: 0,
      units: "Unit",
      totalAmount: 0,
      isExpanded: true,
      tables: [
        {
          id: tableId,
          tableTitle: "Measurement Table",
          rows: initialRows,
        },
      ],
    };

    setSections((prev) =>
      prev.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            subsections: [...section.subsections, newSubsection],
          };
        }
        return section;
      })
    );
  };

  // Add a deduction to a row
  const addDeduction = (sectionId, subsectionId, tableId, rowId) => {
    if (readOnly) return; // Don't allow adding deductions in readOnly mode

    setSections((prev) =>
      prev.map((section) => {
        if (section.id === sectionId && section.subsections) {
          return {
            ...section,
            subsections: section.subsections.map((subsection) => {
              if (subsection.id === subsectionId && subsection.tables) {
                return {
                  ...subsection,
                  tables: subsection.tables.map((table) => {
                    if (table.id === tableId) {
                      return {
                        ...table,
                        rows: table.rows.map((row) => {
                          if (row.id === rowId) {
                            const newDeduction = {
                              id: `deduction-${generateId()}`,
                              description: "Reason for deduction",
                              length: 0,
                              breadth: 0,
                              height: 0,
                              units: row.units,
                              nos: 0,
                              totalQuantity: 0,
                              rate: row.rate,
                              gst: 0,
                              amount: 0,
                            };

                            return {
                              ...row,
                              deductions: [
                                ...(row.deductions || []),
                                newDeduction,
                              ],
                            };
                          }
                          return row;
                        }),
                      };
                    }
                    return table;
                  }),
                };
              }
              return subsection;
            }),
          };
        }
        return section;
      })
    );
  };

  const deleteSubsection = (sectionId, subsectionId) => {
    if (readOnly) return; // Don't allow deleting in readOnly mode
    setSections((prev) =>
      prev.map((section) => {
        if (section.id === sectionId && section.subsections) {
          return {
            ...section,
            subsections: section.subsections.filter(
              (subsection) => subsection.id !== subsectionId
            ),
          };
        }
        return section;
      })
    );
  };
  // Delete a row or deduction
  const deleteItem = (
    sectionId,
    subsectionId,
    tableId,
    rowId,
    deductionId = null
  ) => {
    if (readOnly) return; // Don't allow deleting in readOnly mode

    setSections((prev) =>
      prev.map((section) => {
        if (section.id === sectionId && section.subsections) {
          return {
            ...section,
            subsections: section.subsections.map((subsection) => {
              if (subsection.id === subsectionId && subsection.tables) {
                return {
                  ...subsection,
                  tables: subsection.tables.map((table) => {
                    if (table.id === tableId) {
                      if (deductionId) {
                        // Delete a deduction
                        return {
                          ...table,
                          rows: table.rows.map((row) => {
                            if (row.id === rowId && row.deductions) {
                              return {
                                ...row,
                                deductions: row.deductions.filter(
                                  (d) => d.id !== deductionId
                                ),
                              };
                            }
                            return row;
                          }),
                        };
                      } else {
                        // Delete a row
                        const updatedRows = table.rows.filter(
                          (r) => r.id !== rowId
                        );

                        // Get sorted rows and update sNo values
                        const sortedUpdatedRows = [...updatedRows].sort(
                          (a, b) => {
                            const aNo = parseInt(a.sNo, 10) || 0;
                            const bNo = parseInt(b.sNo, 10) || 0;
                            return aNo - bNo;
                          }
                        );

                        // Update sNo values sequentially
                        const reorderedRows = sortedUpdatedRows.map(
                          (row, index) => ({
                            ...row,
                            sNo: index + 1,
                          })
                        );

                        return {
                          ...table,
                          rows: reorderedRows,
                        };
                      }
                    }
                    return table;
                  }),
                };
              }
              return subsection;
            }),
          };
        }
        return section;
      })
    );
  };

  // Add a new table to a subsection
  const addTable = (sectionId, subsectionId) => {
    if (readOnly) return; // Don't allow adding tables in readOnly mode

    // Create unique IDs
    const tableId = `table-${generateId()}`;

    // Create 5 initial rows
    const initialRows = Array(5)
      .fill()
      .map((_, index) => {
        return {
          id: `row-${generateId()}`,
          sNo: index + 1,
          description: index === 0 ? "New Work Item" : "",
          length: 0,
          breadth: 0,
          height: 0,
          units: "Unit",
          nos: 0,
          totalQuantity: 0,
          rate: 0,
          amount: 0,
          gst: 0,
          deductions: [],
        };
      });

    // Create new table
    const newTable = {
      id: tableId,
      tableTitle: "New Table",
      rows: initialRows,
    };

    // If subsectionId is null or undefined, create a default one
    const effectiveSubsectionId = subsectionId || "subsection-1";

    // Update sections
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;

        // If no subsections exist, create a default one
        if (!section.subsections || section.subsections.length === 0) {
          return {
            ...section,
            subsections: [
              {
                id: "subsection-1",
                title: `${
                  section.id.charAt(0).toUpperCase() + section.id.slice(1)
                } Item ${generateId().slice(0, 4)}`,
                subtitle: "New Work Item Description",
                poQuantity: 0,
                totalClaimedQuantity: 0,
                totalApprovedQuantity: 0,
                units: "Unit",
                totalAmount: 0,
                isExpanded: true,
                tables: [newTable],
              },
            ],
          };
        }

        return {
          ...section,
          subsections: section.subsections.map((subsection) => {
            if (subsection.id !== effectiveSubsectionId) return subsection;

            // Add the new table to this subsection
            const tables = Array.isArray(subsection.tables)
              ? [...subsection.tables]
              : [];
            return {
              ...subsection,
              tables: [...tables, newTable],
            };
          }),
        };
      })
    );
  };

  // Handle drag-and-drop of rows
  const handleDragEnd = (result, sectionId, subsectionId, tableId) => {
    if (readOnly) return; // Don't allow drag-n-drop in readOnly mode
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    if (sourceIndex === destIndex) return;

    setSections((prev) =>
      prev.map((section) => {
        if (section.id === sectionId && section.subsections) {
          return {
            ...section,
            subsections: section.subsections.map((subsection) => {
              if (subsection.id === subsectionId && subsection.tables) {
                return {
                  ...subsection,
                  tables: subsection.tables.map((table) => {
                    if (table.id === tableId) {
                      // Use the sorted rows to ensure consistent order
                      const sortedRows = getSortedRowsBySNo(table.rows);

                      // Move the row as per drag and drop operation
                      const [movedRow] = sortedRows.splice(sourceIndex, 1);
                      sortedRows.splice(destIndex, 0, movedRow);

                      // Update sNo values sequentially starting from 1
                      const reorderedRows = sortedRows.map((row, index) => ({
                        ...row,
                        sNo: index + 1,
                      }));

                      return {
                        ...table,
                        rows: reorderedRows,
                      };
                    }
                    return table;
                  }),
                };
              }
              return subsection;
            }),
          };
        }
        return section;
      })
    );
  };

  // Calculate totals for a table
  const calculateTableTotals = (section, subsection, tableId) => {
    const sec = sections.find((s) => s.id === section);
    if (!sec || !sec.subsections)
      return {
        total: 0,
        deductions: 0,
        net: 0,
        previousTotal: 0,
        presentTotal: 0,
      };

    const sub = sec.subsections.find((s) => s.id === subsection);
    if (!sub || !sub.tables)
      return {
        total: 0,
        deductions: 0,
        net: 0,
        previousTotal: 0,
        presentTotal: 0,
      };

    const table = sub.tables.find((t) => t.id === tableId);
    if (!table)
      return {
        total: 0,
        deductions: 0,
        net: 0,
        previousTotal: 0,
        presentTotal: 0,
      };

    let total = 0;
    let deductions = 0;
    let previousTotal = 0;
    let presentTotal = 0;

    // Use sorted rows for calculation
    getSortedRowsBySNo(table.rows).forEach((row) => {
      const rowAmount = parseFloat(row.amount) || 0;
      total += rowAmount;

      // Track previous vs present amounts
      if (row.isPrevious) {
        previousTotal += rowAmount;
      } else {
        presentTotal += rowAmount;
      }

      if (row.deductions) {
        let rowDeductions = 0;
        row.deductions.forEach((deduction) => {
          const deductAmount = parseFloat(deduction.amount) || 0;
          deductions += deductAmount;
          rowDeductions += deductAmount;
        });

        // Deduct from the appropriate total
        if (row.isPrevious) {
          previousTotal -= rowDeductions;
        } else {
          presentTotal -= rowDeductions;
        }
      }
    });

    return {
      total,
      deductions,
      net: total - deductions,
      previousTotal,
      presentTotal,
    };
  };

  // Helper function to convert UI data structure to API format
  const convertToApiFormat = () => {
    const apiData = {
      billNo: billNumber,
      billType,
      date: billDate,
      poId: paymentDetails.poId,
      client: {
        name: clientDetails.clientName,
        location: clientDetails.location,
      },
      vendor: {
        name: vendorDetails.name,
        firm: vendorDetails.firm,
        category: vendorDetails.category,
      },
      personDetails: { ...personDetails },
      paymentDetails: { ...paymentDetails },
      remarks: finalRemark,
      raisedBy: approvalDetails.raisedBy,
      raisedAt: approvalDetails.raisedDate,
      checkedBy: approvalDetails.checkedBy,
      checkedAt: approvalDetails.checkedDate,
      approvedBy: approvalDetails.approvedBy,
      approvedAt: approvalDetails.approvedDate,
      status: "Requested", // New bills are always in Requested status
      previousBill: billSummary.previousBill,
      presentBill: billedAmount,
      billedAmount: billedAmount,
      totalAmount:
        (parseFloat(billSummary.previousBill) || 0) +
        (parseFloat(billedAmount) || 0),
      retentionPercentage: billSummary.retentionPercentage,
      retentionMoney:
        ((parseFloat(billedAmount) || 0) *
          (parseFloat(billSummary.retentionPercentage) || 0)) /
        100,
      previousTotalAmount: billSummary.previousTotalAmount,
      netPayable:
        (parseFloat(billSummary.previousBill) || 0) +
        (parseFloat(billedAmount) || 0) -
        ((parseFloat(billedAmount) || 0) *
          (parseFloat(billSummary.retentionPercentage) || 0)) /
          100 -
        (parseFloat(totalPaidAmount) || 0),
      workOrderImages: workOrderImages,
      additionalImages: additionalImages,
      workOrderItems: {},
      additionalItems: {},
    };

    // Process sections
    sections.forEach((section) => {
      if (!section.subsections) return;

      // Determine which API section to use
      const apiSection =
        section.id === "workOrderItems"
          ? apiData.workOrderItems
          : apiData.additionalItems;

      // Process subsections
      section.subsections.forEach((subsection) => {
        const subsectionId = subsection.id;

        // Create subsection in API format
        apiSection[subsectionId] = {
          approvedQuantity: parseFloat(subsection.totalApprovedQuantity) || 0,
          category: subsection.title || "", // title is the category
          claimedQuantity: parseFloat(subsection.totalClaimedQuantity) || 0,
          poQuantity: parseFloat(subsection.poQuantity) || 0,
          section: subsection.subtitle || "Description", // subtitle is the description/section
          unit: subsection.units,
          items: {},
          tables: {}, // Add tables array to store table information
        };

        // Process tables and rows
        if (subsection.tables) {
          subsection.tables.forEach((table) => {
            const tableId = table.id;

            // Save table information
            apiSection[subsectionId].tables[tableId] = {
              title: table.tableTitle || "Measurement Table",
              id: tableId,
            };

            // Use sorted rows for conversion and filter out rows marked as previous
            getSortedRowsBySNo(table.rows).forEach((row) => {
              // Skip empty rows and rows from previous bills
              if (
                (!row.description && parseFloat(row.amount || 0) === 0) ||
                row.isPrevious
              )
                return;

              // Add row to items with tableId to track which table it belongs to
              apiSection[subsectionId].items[row.id] = {
                amount: parseFloat(row.amount) || 0,
                description: row.description,
                dimensions: {
                  length: parseFloat(row.length) || 0,
                  breadth: parseFloat(row.breadth) || 0,
                  height: parseFloat(row.height) || 0,
                },
                index: parseInt(row.sNo, 10), // Use sNo as the index for consistent ordering
                nos: parseFloat(row.nos) || 0,
                rate: parseFloat(row.rate) || 0,
                gst: parseFloat(row.gst) || 0,
                totalQuantity: parseFloat(row.totalQuantity) || 0,
                units: row.units,
                tableId: tableId, // Add tableId to track which table this row belongs to
              };

              // Process deductions if any
              if (row.deductions && row.deductions.length > 0) {
                apiSection[subsectionId].items[row.id].deductions = {};

                row.deductions.forEach((deduction) => {
                  // Skip empty deductions
                  if (
                    !deduction.description &&
                    parseFloat(deduction.amount || 0) === 0
                  )
                    return;

                  apiSection[subsectionId].items[row.id].deductions[
                    deduction.id
                  ] = {
                    amount: parseFloat(deduction.amount) || 0,
                    description: deduction.description,
                    dimensions: {
                      length: parseFloat(deduction.length) || 0,
                      breadth: parseFloat(deduction.breadth) || 0,
                      height: parseFloat(deduction.height) || 0,
                    },
                    nos: parseFloat(deduction.nos) || 0,
                    rate: parseFloat(deduction.rate) || 0,
                    gst: parseFloat(deduction.gst) || 0,
                    totalQuantity: parseFloat(deduction.totalQuantity) || 0,
                    units: deduction.units,
                  };
                });
              }
            });
          });
        }
      });
    });

    return apiData;
  };
  const convertToApiFormatForBilledAmount = () => {
    const apiData = {
      billNo: billNumber,
      billType,
      date: billDate,
      poId: paymentDetails.poId,
      client: {
        name: clientDetails.clientName,
        location: clientDetails.location,
      },
      vendor: {
        name: vendorDetails.name,
        firm: vendorDetails.firm,
        category: vendorDetails.category,
      },
      personDetails: { ...personDetails },
      paymentDetails: { ...paymentDetails },
      remarks: finalRemark,
      raisedBy: approvalDetails.raisedBy,
      raisedAt: approvalDetails.raisedDate,
      checkedBy: approvalDetails.checkedBy,
      checkedAt: approvalDetails.checkedDate,
      approvedBy: approvalDetails.approvedBy,
      approvedAt: approvalDetails.approvedDate,
      status: "Requested", // New bills are always in Requested status
      previousBill: billSummary.previousBill,
      presentBill: billedAmount,
      billedAmount: billedAmount,
      totalAmount: billSummary.totalAmount,
      retentionPercentage: billSummary.retentionPercentage,
      retentionMoney: billSummary.retentionMoney,
      previousTotalAmount: billSummary.previousTotalAmount,
      alreadyPaidAmount: paidAmount,
      workOrderImages: workOrderImages,
      additionalImages: additionalImages,
      workOrderItems: {},
      additionalItems: {},
    };

    // Process sections
    sections.forEach((section) => {
      if (!section.subsections) return;

      // Determine which API section to use
      const apiSection =
        section.id === "workOrderItems"
          ? apiData.workOrderItems
          : apiData.additionalItems;

      // Process subsections
      section.subsections.forEach((subsection) => {
        const subsectionId = subsection.id;

        // Create subsection in API format
        apiSection[subsectionId] = {
          approvedQuantity: parseFloat(subsection.totalApprovedQuantity) || 0,
          category: subsection.title || "", // title is the category
          claimedQuantity: parseFloat(subsection.totalClaimedQuantity) || 0,
          poQuantity: parseFloat(subsection.poQuantity) || 0,
          section: subsection.subtitle || "Description", // subtitle is the description/section
          unit: subsection.units,
          items: {},
          tables: {}, // Add tables array to store table information
        };

        // Process tables and rows
        if (subsection.tables) {
          subsection.tables.forEach((table) => {
            const tableId = table.id;

            // Save table information
            apiSection[subsectionId].tables[tableId] = {
              title: table.tableTitle || "Measurement Table",
              id: tableId,
            };

            // Use sorted rows for conversion and filter out rows marked as previous
            getSortedRowsBySNo(table.rows).forEach((row) => {
              // Skip empty rows and rows from previous bills
              if (
                (!row.description && parseFloat(row.amount || 0) === 0) ||
                row.isPrevious
              )
                return;

              // Add row to items with tableId to track which table it belongs to
              apiSection[subsectionId].items[row.id] = {
                amount: parseFloat(row.amount) || 0,
                description: row.description,
                dimensions: {
                  length: parseFloat(row.length) || 0,
                  breadth: parseFloat(row.breadth) || 0,
                  height: parseFloat(row.height) || 0,
                },
                index: parseInt(row.sNo, 10), // Use sNo as the index for consistent ordering
                nos: parseFloat(row.nos) || 0,
                rate: parseFloat(row.rate) || 0,
                gst: parseFloat(row.gst) || 0,
                totalQuantity: parseFloat(row.totalQuantity) || 0,
                units: row.units,
                tableId: tableId, // Add tableId to track which table this row belongs to
              };

              // Process deductions if any
              if (row.deductions && row.deductions.length > 0) {
                apiSection[subsectionId].items[row.id].deductions = {};

                row.deductions.forEach((deduction) => {
                  // Skip empty deductions
                  if (
                    !deduction.description &&
                    parseFloat(deduction.amount || 0) === 0
                  )
                    return;

                  apiSection[subsectionId].items[row.id].deductions[
                    deduction.id
                  ] = {
                    amount: parseFloat(deduction.amount) || 0,
                    description: deduction.description,
                    dimensions: {
                      length: parseFloat(deduction.length) || 0,
                      breadth: parseFloat(deduction.breadth) || 0,
                      height: parseFloat(deduction.height) || 0,
                    },
                    nos: parseFloat(deduction.nos) || 0,
                    rate: parseFloat(deduction.rate) || 0,
                    gst: parseFloat(deduction.gst) || 0,
                    totalQuantity: parseFloat(deduction.totalQuantity) || 0,
                    units: deduction.units,
                  };
                });
              }
            });
          });
        }
      });
    });

    return apiData;
  };
  const convertToApiFormatForPaidAmount = () => {
    // Calculate total paid amount from all previous bills
    let totalPaidAmount = 0;
    if (relatedBills && relatedBills.length > 0 && currentBillIndex > 0) {
      for (let i = 0; i < currentBillIndex; i++) {
        const bill = relatedBills[i];
        if (bill && bill.status === "Paid") {
          totalPaidAmount += parseFloat(bill.paidAmount || 0);
        }
      }
    }

    // Update bill summary with correct already paid amount
    setBillSummary((prev) => ({
      ...prev,
      netPayable: prev.totalAmount - prev.retentionMoney - totalPaidAmount,
    }));

    const apiData = {
      billNo: billNumber,
      billType,
      date: billDate,
      poId: paymentDetails.poId,
      client: {
        name: clientDetails.clientName,
        location: clientDetails.location,
      },
      vendor: {
        name: vendorDetails.name,
        firm: vendorDetails.firm,
        category: vendorDetails.category,
      },
      personDetails: { ...personDetails },
      paymentDetails: { ...paymentDetails },
      remarks: finalRemark,
      raisedBy: approvalDetails.raisedBy,
      raisedAt: approvalDetails.raisedDate,
      checkedBy: approvalDetails.checkedBy,
      checkedAt: approvalDetails.checkedDate,
      approvedBy: approvalDetails.approvedBy,
      approvedAt: approvalDetails.approvedDate,
      status: "Paid", // Set to Paid for paid bills
      previousBill: billSummary.previousBill,
      presentBill: billedAmount,
      billedAmount: billedAmount,
      paidAmount: paidAmount,
      totalAmount:
        (parseFloat(billSummary.previousBill) || 0) +
        (parseFloat(billedAmount) || 0),
      retentionPercentage: billSummary.retentionPercentage,
      retentionMoney:
        ((parseFloat(billedAmount) || 0) *
          (parseFloat(billSummary.retentionPercentage) || 0)) /
        100,
      previousTotalAmount: billSummary.previousTotalAmount,
      netPayable:
        (parseFloat(billSummary.previousBill) || 0) +
        (parseFloat(billedAmount) || 0) -
        ((parseFloat(billedAmount) || 0) *
          (parseFloat(billSummary.retentionPercentage) || 0)) /
          100 -
        (parseFloat(totalPaidAmount) || 0),
      workOrderImages: workOrderImages,
      additionalImages: additionalImages,
      workOrderItems: {},
      additionalItems: {},
    };

    // Process sections
    sections.forEach((section) => {
      if (!section.subsections) return;

      // Determine which API section to use
      const apiSection =
        section.id === "workOrderItems"
          ? apiData.workOrderItems
          : apiData.additionalItems;

      // Process subsections
      section.subsections.forEach((subsection) => {
        const subsectionId = subsection.id;

        // Create subsection in API format
        apiSection[subsectionId] = {
          approvedQuantity: parseFloat(subsection.totalApprovedQuantity) || 0,
          category: subsection.title || "", // title is the category
          claimedQuantity: parseFloat(subsection.totalClaimedQuantity) || 0,
          poQuantity: parseFloat(subsection.poQuantity) || 0,
          section: subsection.subtitle || "Description", // subtitle is the description/section
          unit: subsection.units,
          items: {},
          tables: {}, // Add tables array to store table information
        };

        // Process tables and rows
        if (subsection.tables) {
          subsection.tables.forEach((table) => {
            const tableId = table.id;

            // Save table information
            apiSection[subsectionId].tables[tableId] = {
              title: table.tableTitle || "Measurement Table",
              id: tableId,
            };

            // Use sorted rows for conversion and filter out rows marked as previous
            getSortedRowsBySNo(table.rows).forEach((row) => {
              // Skip empty rows and rows from previous bills
              if (
                (!row.description && parseFloat(row.amount || 0) === 0) ||
                row.isPrevious
              )
                return;

              // Add row to items with tableId to track which table it belongs to
              apiSection[subsectionId].items[row.id] = {
                amount: parseFloat(row.amount) || 0,
                description: row.description,
                dimensions: {
                  length: parseFloat(row.length) || 0,
                  breadth: parseFloat(row.breadth) || 0,
                  height: parseFloat(row.height) || 0,
                },
                index: parseInt(row.sNo, 10), // Use sNo as the index for consistent ordering
                nos: parseFloat(row.nos) || 0,
                rate: parseFloat(row.rate) || 0,
                gst: parseFloat(row.gst) || 0,
                totalQuantity: parseFloat(row.totalQuantity) || 0,
                units: row.units,
                tableId: tableId, // Add tableId to track which table this row belongs to
              };

              // Process deductions if any
              if (row.deductions && row.deductions.length > 0) {
                apiSection[subsectionId].items[row.id].deductions = {};

                row.deductions.forEach((deduction) => {
                  // Skip empty deductions
                  if (
                    !deduction.description &&
                    parseFloat(deduction.amount || 0) === 0
                  )
                    return;

                  apiSection[subsectionId].items[row.id].deductions[
                    deduction.id
                  ] = {
                    amount: parseFloat(deduction.amount) || 0,
                    description: deduction.description,
                    dimensions: {
                      length: parseFloat(deduction.length) || 0,
                      breadth: parseFloat(deduction.breadth) || 0,
                      height: parseFloat(deduction.height) || 0,
                    },
                    nos: parseFloat(deduction.nos) || 0,
                    rate: parseFloat(deduction.rate) || 0,
                    gst: parseFloat(deduction.gst) || 0,
                    totalQuantity: parseFloat(deduction.totalQuantity) || 0,
                    units: deduction.units,
                  };
                });
              }
            });
          });
        }
      });
    });

    return apiData;
  };
  // Handle bill approval or decline
  const handleBillApproval = async (newStatus) => {
    try {
      setIsSubmitting(true);
      setLoadingMessage(
        `${newStatus === "Approved" ? "Approving" : "Declining"} bill...`
      );
      setLoadingProgress(30);

      // Format data for API
      const formattedData = convertToApiFormat();

      // Update status and approval details
      formattedData.status = newStatus;
      formattedData.approvedBy = userContext.user?.DisplayName || "Admin";
      formattedData.approvedAt = `${
        formattedData.approvedBy
      } ${formatCurrentDate()} ${new Date().toLocaleTimeString()}`;

      setLoadingProgress(60);

      // Save the updated bill
      const updateResponse = await OrdersBillingAPI.UpdateBillingInformation(
        poId,
        billId,
        formattedData
      );

      setLoadingProgress(90);

      if (updateResponse && updateResponse.isError) {
        throw new Error(
          updateResponse.errorMessage || "Failed to update bill status"
        );
      }

      // Show success message
      toast.success(`Bill ${newStatus.toLowerCase()} successfully`);
      setLoadingProgress(100);

      // Return to billing page after a short delay
      setTimeout(() => {
        history.push("/Billing");
      }, 1000);
    } catch (error) {
      console.error(`Error ${newStatus.toLowerCase()} bill:`, error);
      toast.error(
        `Error ${newStatus.toLowerCase()} bill: ${
          error.message || "Unknown error"
        }`
      );
      setLoadingProgress(0);
    } finally {
      setIsSubmitting(false);
      setLoadingMessage("");
    }
  };

  // Save bill
  const saveBill = async () => {
    console.log("Saving bill...");
    try {
      setIsSubmitting(true);
      setLoadingMessage("Saving bill...");
      setLoadingProgress(30);

      // Format data for API using the helper function
      const formattedData = convertToApiFormat();

      // If editing an approved bill and changes were made, change status to Requested
      if (billStatus === "Approved" && isEditingApprovedBill && hasChanges) {
        formattedData.status = "Requested";
        setBillStatus("Requested");
        toast.info("Bill status changed to Requested for re-approval");
      }

      setLoadingProgress(60);

      // Make the actual API call
      let response;
      if (viewMode && existingBillData) {
        // Update existing bill
        response = await OrdersBillingAPI.UpdateBillingInformation(
          poId,
          billId,
          formattedData
        );
      } else {
        // Create new bill
        response = await OrdersBillingAPI.SaveBill(poId, billId, formattedData);
      }

      setLoadingProgress(90);

      if (response && response.isError) {
        throw new Error(response.errorMessage || "Failed to save bill");
      }

      // Reset edit mode and changes flag after successful save
      if (isEditingApprovedBill) {
        setIsEditingApprovedBill(false);
        setHasChanges(false);
      }

      setLoadingProgress(100);
      setLoadingMessage("Bill saved successfully!");

      toast.success("Bill saved successfully!");
      setTimeout(() => {
        history.push("/Billing");
      }, 1000);
    } catch (error) {
      console.error("Error saving bill:", error);
      toast.error("Error saving bill: " + (error.message || "Unknown error"));
    } finally {
      setIsSubmitting(false);
      setLoadingMessage("");
      setLoadingProgress(0);
    }
  };

  const saveDraft = async () => {
    console.log("Saving draft...");
    try {
      setIsSubmitting(true);
      setLoadingMessage("Saving draft...");
      setLoadingProgress(30);

      // Format data for API using the helper function
      const formattedData = convertToApiFormat();

      // If editing an approved bill and changes were made, change status to Requested
      formattedData.status = "Draft";
      setBillStatus("Draft");

      toast.info("Bill status changed to Draft for further editing");

      setLoadingProgress(60);

      // Make the actual API call
      let response;
      if (viewMode && existingBillData) {
        // Update existing bill
        response = await OrdersBillingAPI.UpdateBillingInformation(
          poId,
          billId,
          formattedData
        );
      } else {
        // Create new bill
        response = await OrdersBillingAPI.SaveBill(poId, billId, formattedData);
      }

      setLoadingProgress(90);

      if (response && response.isError) {
        throw new Error(response.errorMessage || "Failed to save bill");
      }

      // Reset edit mode and changes flag after successful save
      if (isEditingApprovedBill) {
        setIsEditingApprovedBill(false);
        setHasChanges(false);
      }

      setLoadingProgress(100);
      setLoadingMessage("Bill saved successfully!");

      toast.success("Bill saved successfully!");
      setTimeout(() => {
        history.push("/Billing");
      }, 1000);
    } catch (error) {
      console.error("Error saving bill:", error);
      toast.error("Error saving bill: " + (error.message || "Unknown error"));
    } finally {
      setIsSubmitting(false);
      setLoadingMessage("");
      setLoadingProgress(0);
    }
  };

  // Editable field component
  const EditableField = ({
    id,
    value,
    onChange,
    isNumeric = false,
    className = "",
    prefix = "",
    suffix = "",
    isPrevious = false, // Add flag for previous bill items
  }) => {
    // Allow editing all fields, even those from previous bills
    const isEditing = editingField === id && !readOnly;
    const inputRef = useRef(null);
    const [localValue, setLocalValue] = useState(value);
    const [hasFocus, setHasFocus] = useState(false);

    // Update local value when external value changes, but only if not editing
    useEffect(() => {
      if (!hasFocus) {
        setLocalValue(value);
      }
    }, [value, hasFocus]);

    // When entering edit mode, focus on the input field and disable dragging
    useEffect(() => {
      if (isEditing && inputRef.current) {
        setHasFocus(true);
        inputRef.current.focus();
        setIsDraggingDisabled(true);
      } else if (!isEditing && editingField === id) {
        setIsDraggingDisabled(false);
        setHasFocus(false);
      }
    }, [isEditing, id]);

    // Ensure values are saved when component unmounts
    useEffect(() => {
      return () => {
        if (hasFocus && localValue !== value) {
          saveChanges();
        }
      };
    }, [hasFocus, localValue, value]);
    const handleClick = (e) => {
      // Prevent event propagation
      e.stopPropagation();

      // Prevent editing if in read-only mode or if this field is from a previous bill
      if (readOnly || isPrevious) {
        return;
      }

      // Set the editingField state to this field's ID
      setEditingField(id);

      // Force focus on the input after setting isEditing to true
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 0);
    };

    const handleInputChange = (e) => {
      e.preventDefault();
      e.stopPropagation();
      let newValue = e.target.value;

      // Update local value immediately for responsive UI
      setLocalValue(newValue);

      // Call onChange immediately to update parent state
      // This ensures typing is reflected right away without needing double input
      onChange(
        isNumeric && newValue !== ""
          ? !isNaN(parseFloat(newValue))
            ? parseFloat(newValue)
            : newValue
          : newValue
      );
    };

    // Save changes to ensure they're preserved
    const saveChanges = () => {
      // Changes are already applied in handleInputChange
      // This is now just a backup to ensure nothing is missed
      if (localValue !== value) {
        // Convert numeric values if needed
        let finalValue = localValue;
        if (isNumeric && finalValue !== "") {
          if (!isNaN(parseFloat(finalValue))) {
            finalValue = parseFloat(finalValue);
          }
        }
        onChange(finalValue);
      }
    };

    const handleBlur = (e) => {
      // Don't close if clicking on another part of the same input
      if (e.currentTarget.contains(e.relatedTarget)) {
        return;
      }

      saveChanges();
      setEditingField(null);
      setHasFocus(false);
    };

    const handleKeyDown = (e) => {
      e.stopPropagation();

      if (e.key === "Enter") {
        saveChanges();
        e.preventDefault();
        // Delay setting editingField to null to ensure state update
        setTimeout(() => {
          setEditingField(null);
          setHasFocus(false);
        }, 50);
      } else if (e.key === "Escape") {
        // Revert to the original value on escape
        setLocalValue(value);
        setEditingField(null);
        setHasFocus(false);
        e.preventDefault();
      } else if (e.key === "Tab") {
        // Allow tab navigation - don't stop propagation
        saveChanges();
        setEditingField(null);
        setHasFocus(false);

        // Find all editable fields in the table and determine the next one
        const allFields = document.querySelectorAll(".editable-field");
        const fieldArray = Array.from(allFields);
        const currentIndex = fieldArray.findIndex(
          (field) => field.getAttribute("data-field-id") === id
        );

        if (currentIndex !== -1 && currentIndex < fieldArray.length - 1) {
          const nextField = fieldArray[currentIndex + 1];
          const nextFieldId = nextField.getAttribute("data-field-id");

          // Small delay to allow the current field to finish blur event
          setTimeout(() => {
            setEditingField(nextFieldId);
          }, 10);
        }
      }
    };

    // Handle focus to track when component is being edited
    const handleFocus = () => {
      setHasFocus(true);
    };

    // Handle clicks outside to close editor
    useEffect(() => {
      if (!isEditing) return;

      const handleClickOutside = (e) => {
        if (inputRef.current && !inputRef.current.contains(e.target)) {
          saveChanges();
          setEditingField(null);
          setHasFocus(false);
        }
      };

      // Add global event listener
      document.addEventListener("mousedown", handleClickOutside);

      // Clean up
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isEditing, localValue]);

    // Determine the display value
    const displayValue =
      localValue !== undefined && localValue !== null ? localValue : "";

    return (
      <div
        className={`editable-field ${className} ${isEditing ? "editing" : ""} ${
          isNumeric ? "numeric" : ""
        } ${readOnly ? "readonly" : ""} ${isPrevious ? "previous-data" : ""}`}
        onClick={handleClick}
        data-field-id={id}
      >
        {isEditing ? (
          <input
            ref={inputRef}
            type={isNumeric ? "number" : "text"}
            value={displayValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            style={{
              textAlign: isNumeric ? "right" : "left",
              pointerEvents: "auto",
              width: "100%",
              height: "100%",
            }}
          />
        ) : (
          <div className="field-content" onClick={handleClick}>
            {isNumeric && value !== undefined && value !== null
              ? `${prefix}${formatCurrency(value)}${suffix}`
              : `${prefix}${displayValue}${suffix}`}
          </div>
        )}
      </div>
    );
  };

  // Editable text area component
  const EditableTextArea = ({ id, value, onChange, className = "" }) => {
    const isEditing = editingField === id && !readOnly;
    const textareaRef = useRef(null);
    const [localValue, setLocalValue] = useState(value);
    const [hasFocus, setHasFocus] = useState(false);
    // Add ref to store cursor position
    const cursorPositionRef = useRef(null);

    // Update local value when external value changes, but only if not editing
    useEffect(() => {
      if (!hasFocus) {
        setLocalValue(value);
      }
    }, [value, hasFocus]);

    // When entering edit mode, focus on the textarea
    useEffect(() => {
      if (isEditing && textareaRef.current) {
        setHasFocus(true);
        textareaRef.current.focus();
        // Place cursor at the end of text when starting to edit
        if (
          textareaRef.current &&
          typeof textareaRef.current.value === "string"
        ) {
          const length = textareaRef.current.value.length;
          textareaRef.current.selectionStart = length;
          textareaRef.current.selectionEnd = length;
        }
        setIsDraggingDisabled(true);
      } else if (!isEditing && editingField === id) {
        setIsDraggingDisabled(false);
        setHasFocus(false);
      }
    }, [isEditing, id]);

    // Ensure values are saved when component unmounts
    useEffect(() => {
      return () => {
        if (hasFocus && localValue !== value) {
          saveChanges();
        }
      };
    }, [hasFocus, localValue, value]);

    const handleClick = (e) => {
      if (readOnly) return; // Don't allow editing in readOnly mode

      e.preventDefault();
      e.stopPropagation();
      if (!isEditing) {
        setEditingField(id);
      }
    };

    const handleInputChange = (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Store the current cursor position before the update
      if (textareaRef.current) {
        cursorPositionRef.current = {
          start: textareaRef.current.selectionStart,
          end: textareaRef.current.selectionEnd,
        };
      }

      // Update local value immediately for responsive UI
      setLocalValue(e.target.value);

      // Call onChange immediately to update parent state
      // This ensures typing is reflected right away without needing double input
      onChange(e.target.value);

      // Restore cursor position after state update
      setTimeout(() => {
        if (textareaRef.current && cursorPositionRef.current) {
          textareaRef.current.selectionStart = cursorPositionRef.current.start;
          textareaRef.current.selectionEnd = cursorPositionRef.current.end;
        }
      }, 0);
    };

    // Save changes to ensure they're preserved
    const saveChanges = () => {
      // Changes are already applied in handleInputChange
      // This is now just a backup to ensure nothing is missed
      if (localValue !== value) {
        onChange(localValue);
      }
    };

    const handleBlur = (e) => {
      // Don't close if clicking on another part of the same input
      if (e.currentTarget.contains(e.relatedTarget)) {
        return;
      }

      saveChanges();
      setEditingField(null);
      setHasFocus(false);
    };

    // Handle focus to track when component is being edited
    const handleFocus = () => {
      setHasFocus(true);
    };

    const handleKeyDown = (e) => {
      e.stopPropagation();

      if (e.key === "Enter" && e.ctrlKey) {
        saveChanges();
        setEditingField(null);
        setHasFocus(false);
        e.preventDefault();
      } else if (e.key === "Escape") {
        // Revert to the original value on escape
        setLocalValue(value);
        setEditingField(null);
        setHasFocus(false);
        e.preventDefault();
      } else if (e.key === "Tab") {
        // Allow tab navigation between fields
        saveChanges();
        setEditingField(null);
        setHasFocus(false);
      }
    };

    // Handle clicks outside to close editor
    useEffect(() => {
      if (!isEditing) return;

      const handleClickOutside = (e) => {
        if (textareaRef.current && !textareaRef.current.contains(e.target)) {
          saveChanges();
          setEditingField(null);
          setHasFocus(false);
        }
      };

      // Add global event listener
      document.addEventListener("mousedown", handleClickOutside);

      // Clean up
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isEditing, localValue]);

    // Determine the display value
    const displayValue =
      localValue !== undefined && localValue !== null ? localValue : "";

    return (
      <div
        className={`editable-field ${className} ${isEditing ? "editing" : ""} ${
          readOnly ? "readonly" : ""
        }`}
        onClick={handleClick}
        data-field-id={id}
      >
        {isEditing ? (
          <textarea
            ref={textareaRef}
            value={displayValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            rows={4}
            style={{
              width: "100%",
              pointerEvents: "auto",
            }}
          />
        ) : (
          <div className="field-content textarea-content" onClick={handleClick}>
            {displayValue}
          </div>
        )}
      </div>
    );
  };
  // Calculate bill summary data (totals, paid, due)
  useEffect(() => {
    if (relatedBills.length > 0) {
      let totalAmount = 0;
      let totalPaid = 0;
      let totalDue = 0;

      relatedBills.forEach((bill) => {
        const amount = parseFloat(bill.totalAmount || 0);
        // Use the explicit paidAmount if available, otherwise 0 (don't assume paid)
        const amountPaid = parseFloat(bill.paidAmount || 0);

        totalAmount += amount;

        // Calculate paid and due amounts properly
        if (bill.status === "Paid") {
          // For paid bills, add the paidAmount to totalPaid
          totalPaid += amountPaid;

          // Add remaining amount (if any) to totalDue
          const remainingDue = amount - amountPaid;
          if (remainingDue > 0) {
            totalDue += remainingDue;
          }
        } else {
          // For any other status, the full amount is due
          totalDue += amount;
        }
      });

      setBillsSummary({
        totalAmount,
        totalPaid,
        totalDue,
      });
    }
  }, [relatedBills]);

  // Handle view more bills
  const handleViewMoreBills = () => {
    setShowAllBills(true);
  };

  // Close all bills modal
  const closeAllBillsModal = () => {
    setShowAllBills(false);
  };

  // Toggle between "View All Bills" and "View Total"
  const toggleBillSummaryView = () => {
    setShowTotalView(!showTotalView);
  };

  // Toggle edit mode for approved bills
  const toggleApprovedBillEditMode = () => {
    if (billStatus === "Approved") {
      const newEditMode = !isEditingApprovedBill;
      setIsEditingApprovedBill(newEditMode);

      // Reset hasChanges when exiting edit mode without saving
      if (!newEditMode && hasChanges) {
        setHasChanges(false);
        toast.info("Changes discarded. Bill remains in Approved status.");
      }
    }
  };

  // Helper function to calculate total amount from bill data
  const calculateTotalFromBillData = (billData) => {
    let total = 0;

    // Add up work order items
    if (billData.workOrderItems) {
      Object.values(billData.workOrderItems).forEach((subsection) => {
        if (subsection.items) {
          Object.values(subsection.items).forEach((item) => {
            // Skip items from previous bills (which would be marked with isPrevious=true in the UI)
            // Here we can only infer based on item data since the API doesn't store isPrevious flag
            if (!item.fromPreviousBill) {
              total += parseFloat(item.amount) || 0;

              // Subtract deductions if any
              if (item.deductions) {
                Object.values(item.deductions).forEach((deduction) => {
                  total -= parseFloat(deduction.amount) || 0;
                });
              }
            }
          });
        }
      });
    }

    // Add up additional items
    if (billData.additionalItems) {
      Object.values(billData.additionalItems).forEach((subsection) => {
        if (subsection.items) {
          Object.values(subsection.items).forEach((item) => {
            // Skip items from previous bills
            if (!item.fromPreviousBill) {
              total += parseFloat(item.amount) || 0;

              // Subtract deductions if any
              if (item.deductions) {
                Object.values(item.deductions).forEach((deduction) => {
                  total -= parseFloat(deduction.amount) || 0;
                });
              }
            }
          });
        }
      });
    }

    return total;
  };

  // Add this style block near the top of the file, after other style declarations
  // Add CSS for better styling of previous bill items
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .previous-bill-row {
        background-color: #f9f9f9;
        color: #666;
      }
      .previous-bill-row td {
        font-style: italic;
        pointer-events: none;
      }
      .header-row {
        background-color: #f0f0f0;
        font-weight: bold;
        text-align: center;
        color: #444;
      }
      .subheader-row {
        background-color: #eaf5ff;
        font-weight: 500;
        color: #0066cc;
        text-align: center;
      }
      .header-row td, .subheader-row td {
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="raise-bill-wrapper">
      <div className="raise-bill-container">
        {/* Header */}
        <div className="bill-header">
          <div className="header-left">
            <button
              className="back-btn"
              onClick={() => history.push("/Billing")}
            >
              <FiArrowLeft />
            </button>
            <h1>
              {viewMode ? (
                `${billNumber}`
              ) : (
                <>
                  <EditableField
                    id="billNumber"
                    value={billNumber}
                    onChange={(value) => handleFieldEdit("billNumber", value)}
                    className="primary-field"
                  />
                </>
              )}
            </h1>
            {billStatus && (
              <div
                className={`bill-status ${billStatus.toLowerCase()}`}
                style={{
                  marginLeft: "10px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  color:
                    billStatus === "Approved"
                      ? "#155724"
                      : billStatus === "Declined"
                      ? "#721c24"
                      : billStatus === "Paid"
                      ? "#0c5460"
                      : "#856404",
                  border: `1px solid ${
                    billStatus === "Approved"
                      ? "#c3e6cb"
                      : billStatus === "Declined"
                      ? "#f5c6cb"
                      : billStatus === "Paid"
                      ? "#d1ecf1"
                      : "#ffeeba"
                  }`,
                  padding: "10px 20px",
                  alignContent: "center",
                  alignSelf: "center",
                  borderRadius: "6px",
                  backgroundColor:
                    billStatus === "Approved"
                      ? "#d4edda"
                      : billStatus === "Declined"
                      ? "#f8d7da"
                      : billStatus === "Paid"
                      ? "#d1ecf1"
                      : "#fff3cd",
                  display: "inline-block",
                  textAlign: "center",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  transition: "background-color 0.3s ease",
                  cursor: "pointer",
                  marginLeft: "10px",
                }}
              >
                {billStatus}
                {billStatus === "Approved" && isEditingApprovedBill && (
                  <div
                    className="editing-message"
                    style={{
                      fontSize: "12px",
                      marginTop: "5px",
                      color: "#155724",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    <FiEdit2 size={12} /> Editing Mode - Changes will be
                    resubmitted for approval
                  </div>
                )}
                {billStatus === "Declined" && (
                  <div
                    className="declined-message"
                    style={{
                      fontSize: "12px",
                      marginTop: "5px",
                      color: "#721c24",
                    }}
                  >
                    This bill was declined. You can edit and resubmit.
                  </div>
                )}
              </div>
            )}
            {billStatus === "Paid" && (
              <div
                className="billed-amount"
                style={{
                  flexDirection: "row",
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: "10px",
                  backgroundColor: "#d1ecf1",
                  border: "1px solid #bee5eb",

                  borderRadius: "6px",
                  marginLeft: "10px",
                  padding: "10px",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              >
                <div
                  className="info-label"
                  style={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#333",
                    marginRight: "10px",
                    textAlign: "right",
                    width: "120px",
                    flexShrink: 0,
                    display: "inline-block",
                  }}
                >
                  Paid Amount
                </div>
                <span
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",

                    color: "#28a745",
                    textAlign: "right",
                    flexGrow: 1,
                  }}
                >
                  ₹
                  {formatCurrency(
                    paidAmount ? paidAmount : billSummary.totalAmount
                  )}
                </span>
              </div>
            )}
          </div>
          <div className="header-actions">
            {/* Show edit button if bill is in Approved state */}
            {billStatus === "Approved" && !compareBill && (
              <button
                className={`edit-btn ${isEditingApprovedBill ? "active" : ""}`}
                onClick={toggleApprovedBillEditMode}
                title={
                  isEditingApprovedBill
                    ? "Cancel editing"
                    : "Edit approved bill"
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "500",
                  border: "none",
                  outline: "none",
                  cursor: "pointer",
                  transition:
                    "transform 0.15s ease, background-color 0.2s ease",
                  backgroundColor: isEditingApprovedBill
                    ? "#ff4757"
                    : "#3498db",
                  color: "#ffffff",
                  boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                  transform: "translateY(0)",
                  margin: "0 8px",
                  minWidth: "100px",
                  height: "36px",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 8px rgba(0,0,0,0.15)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 5px rgba(0,0,0,0.1)";
                }}
              >
                {isEditingApprovedBill ? (
                  <>
                    <FiX size={16} />
                    <span>Cancel</span>
                  </>
                ) : (
                  <>
                    <FiEdit2 size={16} />
                    <span>Edit</span>
                  </>
                )}
              </button>
            )}

            {/* Show save button when changes are made to an approved bill */}
            {billStatus === "Approved" &&
              isEditingApprovedBill &&
              hasChanges && (
                <button
                  className="save-btn"
                  onClick={() => {
                    if (
                      billedAmount > 0 &&
                      billedAmount !== billSummary.billedAmount
                    ) {
                      openModal();
                    } else {
                      saveBill();
                    }
                  }}
                  title="Save changes (will change status to Requested)"
                >
                  <FiSave size={18} /> Save Changes
                </button>
              )}

            {/* Show bill approval buttons if bill is in Requested or Checked state */}

            {!readOnly && billStatus !== "Approved" && (
              <>
                <button
                  className="save-draft"
                  onClick={() => {
                    saveDraft();
                  }}
                  disabled={isSubmitting}
                >
                  <MdDrafts
                    style={{
                      fontSize: "18px",
                      marginRight: "8px",
                      color: "#1976D2",
                      verticalAlign: "middle",
                    }}
                  />{" "}
                  Save Draft
                </button>
              </>
            )}
            {!readOnly && billStatus !== "Approved" && (
              <>
                <button
                  className="generate-btn"
                  onClick={() => {
                    if (
                      billedAmount > 0 &&
                      billedAmount !== billSummary.billedAmount
                    ) {
                      openModal();
                    } else {
                      if (billStatus === "Draft" && billedAmount > 0) {
                        openModal();
                      } else {
                        saveBill();
                      }
                    }
                  }}
                  disabled={isSubmitting}
                >
                  <FiSave
                    style={{
                      fontSize: "18px",
                      marginRight: "8px",
                      color: "#000",
                      verticalAlign: "middle",
                    }}
                  />{" "}
                  Generate Bill
                </button>
              </>
            )}
          </div>
        </div>

        {/* Main form content */}
        <div className="form-content">
          <StatusProgress status={billStatus} />
          {showApproveDecline ? (
            <div className="approval-actions">
              <button
                className="action-btn approve"
                onClick={() => handleBillApproval("Approved")}
                disabled={isSubmitting}
                aria-label="Approve bill"
              >
                <FiCheck size={18} /> Approve
              </button>
              <button
                className="action-btn decline"
                onClick={() => handleBillApproval("Declined")}
                disabled={isSubmitting}
                aria-label="Decline bill"
              >
                <FiXCircle size={18} /> Decline
              </button>
            </div>
          ) : (
            //show mark as paid button if bill is in Approved state
            billStatus === "Approved" && (
              <div className="approval-actions">
                <button
                  style={{
                    backgroundColor: "#1976D2",
                    color: "#fff",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "21px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "15px",
                    fontWeight: "500",
                    boxShadow: "0 4px 10px rgba(25, 118, 210, 0.2)",
                    transition: "all 0.3s ease",
                    margin: "0 8px",
                    minWidth: "120px",
                    height: "42px",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = "#1565C0";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 12px rgba(25, 118, 210, 0.3)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = "#1976D2";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 10px rgba(25, 118, 210, 0.2)";
                  }}
                  onClick={() => {
                    //show a modal to enter the paid amount default will be present bill amount

                    setPaidAmount(billSummary.billedAmount);
                    setShowModal(true);
                  }}
                  disabled={isSubmitting}
                  aria-label="Mark bill as paid"
                >
                  <FiCheckCircle size={20} /> Mark as Paid
                </button>
              </div>
            )
          )}
          {/* Top info section with client, vendor, and bill details */}
          <div className="info-section">
            <div className="info-card">
              {/* Client name and location at top */}
              {/* <div className="client-header">
                <div className="info-group">
                  <div className="info-label">To</div>
                  <EditableField
                    id="client.clientName"
                    value={clientDetails.clientName}
                    onChange={(value) =>
                      handleFieldEdit("client.clientName", value)
                    }
                    className="primary-field"
                  />
                  <EditableField
                    id="client.location"
                    value={clientDetails.location}
                    onChange={(value) =>
                      handleFieldEdit("client.location", value)
                    }
                    className="secondary-field"
                  />
                </div>
              </div> */}

              {/* Three column layout */}
              <div className="info-row">
                {/* First column - Person details */}
                <div className="info-col left-col">
                  <div className="info-details">
                    <div className="info-detail-row">
                      <div className="info-label">Vendor Name</div>
                      <EditableField
                        id="vendor.name"
                        value={vendorDetails.name}
                        onChange={(value) =>
                          handleFieldEdit("vendor.name", value)
                        }
                      />
                    </div>
                    <div className="info-detail-row">
                      <div className="info-label">Category</div>
                      <EditableField
                        id="person.category"
                        value={personDetails.category}
                        onChange={(value) =>
                          handleFieldEdit("person.category", value)
                        }
                      />
                    </div>
                    <div className="info-detail-row">
                      <div className="info-label">Mobile Number</div>
                      <EditableField
                        id="person.mobileNo"
                        value={personDetails.mobileNo}
                        onChange={(value) =>
                          handleFieldEdit("person.mobileNo", value)
                        }
                      />
                    </div>
                    <div className="info-detail-row">
                      <div className="info-label">PAN NO.</div>
                      <EditableField
                        id="person.gstNo"
                        value={personDetails.panNo}
                        onChange={(value) =>
                          handleFieldEdit("person.panNo", value)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Second column - Bill Info */}
                <div className="info-col middle-col">
                  <div className="bill-details">
                    <div className="info-details">
                      <div className="info-detail-row">
                        <div className="info-label">Bill No.</div>
                        <EditableField
                          id="billNumber"
                          value={billNumber}
                          onChange={(value) =>
                            handleFieldEdit("billNumber", value)
                          }
                        />
                      </div>
                      <div className="info-detail-row">
                        <div className="info-label">Date</div>
                        <EditableField
                          id="billDate"
                          value={billDate}
                          onChange={(value) =>
                            handleFieldEdit("billDate", value)
                          }
                        />
                      </div>
                      <div className="info-detail-row">
                        <div className="info-label">PO ID</div>
                        <EditableField
                          id="payment.poId"
                          value={paymentDetails.poId}
                          onChange={(value) =>
                            handleFieldEdit("payment.poId", value)
                          }
                        />
                      </div>
                      <div className="info-detail-row">
                        <div className="info-label">Aadhar NO.</div>
                        <EditableField
                          id="payment.aadharNo"
                          value={paymentDetails.aadharNo}
                          onChange={(value) =>
                            handleFieldEdit("payment.aadharNo", value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Third column - Vendor details */}
                <div className="info-col right-col">
                  <div className="vendor-details">
                    {/* <div className="info-label">Vendor</div> */}
                    <div className="info-details">
                      <div className="info-detail-row">
                        <div className="info-label">Vendor Firm</div>
                        <EditableField
                          id="vendor.firm"
                          value={vendorDetails.firm}
                          onChange={(value) =>
                            handleFieldEdit("vendor.firm", value)
                          }
                        />
                      </div>
                      <div className="info-detail-row">
                        <div className="info-label">Bank Name</div>
                        <EditableField
                          id="payment.bankName"
                          value={paymentDetails.bankName}
                          onChange={(value) =>
                            handleFieldEdit("payment.bankName", value)
                          }
                        />
                      </div>
                      <div className="info-detail-row">
                        <div className="info-label">IFSC Code</div>
                        <EditableField
                          id="payment.ifscCode"
                          value={paymentDetails.ifscCode}
                          onChange={(value) =>
                            handleFieldEdit("payment.ifscCode", value)
                          }
                        />
                      </div>
                      <div className="info-detail-row">
                        <div className="info-label">Account Number</div>
                        <EditableField
                          id="payment.accountNo"
                          value={paymentDetails.accountNo}
                          onChange={(value) =>
                            handleFieldEdit("payment.accountNo", value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Sections - Work Order Items, Additional Items, etc. */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "20px",
              marginBottom: "20px",
              padding: "10px",
              backgroundColor: "#fff",
              borderRadius: "8px",
              border: "1px solid #14AE5C",
              width: "100%",
            }}
          >
            <div
              className="billed-amount"
              style={{
                flexDirection: "row",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                maxWidth: "600px",
                paddingLeft: "10px",
              }}
            >
              <div className="info-label">Approved Amount</div>
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "#333",
                  textAlign: "right",
                }}
              >
                ₹{formatCurrency(billedAmount)}
              </span>
            </div>

            {relatedBills.length > 0 && (
              <div
                onClick={() => {
                  setCompareBill(!compareBill);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginLeft: "15px",
                  color: "#333",
                  backgroundColor: "#14AE5C",
                  cursor: "pointer",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  alignSelf: "center",
                  justifySelf: "center",
                }}
              >
                <TbLayersDifference
                  size={18}
                  style={{
                    color: "white",
                    marginLeft: "5px",
                    marginRight: "10px",
                    alignSelf: "center",
                    justifySelf: "center",
                  }}
                />
                <button
                  style={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    backgroundColor: "transparent",
                    border: "none",
                    color: "white",
                    cursor: "pointer",

                    textAlign: "center",
                  }}
                >
                  {!compareBill
                    ? "Compare Previous Bills"
                    : "Exit from Compare Bills"}
                </button>
              </div>
            )}
          </div>
          <BillComparisonView
            bills={relatedBills}
            poId={poId}
            compareBill={compareBill}
          />
          {sections.map((section) => (
            <div key={section.id} className="bill-section">
              <div className="section-header">
                <div className="section-title">{section.title}</div>

                <div className="section-toggle">
                  {section.id !== "mileStones" &&
                    section.id !== "workOrderItems" &&
                    !readOnly && (
                      <>
                        workOrderItems
                        <button
                          className="add-subsection-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            addSubsection(section.id);
                          }}
                        >
                          <FiPlus /> Add
                        </button>
                      </>
                    )}
                  <FiChevronDown
                    onClick={() => toggleSection(section.id)}
                    className={section.isExpanded ? "expanded" : ""}
                  />
                </div>
              </div>

              {section.isExpanded && section.id === "workOrderItems" && (
                <div className="section-content">
                  {section.subsections && section.subsections.length > 0 ? (
                    section.subsections.map((subsection) => (
                      <div key={subsection.id} className="subsection">
                        <div
                          className="subsection-header"
                          onClick={() =>
                            toggleSubsection(section.id, subsection.id)
                          }
                        >
                          <div className="subsection-title">
                            {/* <EditableField
                              id={`${section.id}-${subsection.id}-title`}
                              value={subsection.title}
                              onChange={(value) =>
                                handleFieldEdit(
                                  "title",
                                  value,
                                  section.id,
                                  subsection.id
                                )
                              }
                            /> */}
                            <p
                              style={{
                                fontSize: "16px",
                                fontWeight: "bold",
                                color: "#333",
                                marginBottom: "10px",
                              }}
                            >
                              {subsection.title || "New Subsection"}
                            </p>
                            <span className="subtitle">
                              {/* <EditableField
                                id={`${section.id}-${subsection.id}-subtitle`}
                                value={subsection.subtitle}
                                onChange={(value) =>
                                  handleFieldEdit(
                                    "subtitle",
                                    value,
                                    section.id,
                                    subsection.id
                                  )
                                }
                              /> */}
                              <p style={{ fontSize: "14px", color: "#666" }}>
                                {subsection.subtitle || "New Subsection"}
                              </p>
                            </span>
                          </div>
                          <div className="subsection-info">
                            <div className="info-item">
                              <label>PO Quantity</label>
                              <EditableField
                                id={`${section.id}-${subsection.id}-poQuantity`}
                                value={subsection.poQuantity}
                                onChange={(value) =>
                                  handleFieldEdit(
                                    "poQuantity",
                                    value,
                                    section.id,
                                    subsection.id
                                  )
                                }
                                isNumeric={true}
                              />
                            </div>
                            <div className="info-item">
                              <label>Total Claimed Quantity</label>
                              <EditableField
                                id={`${section.id}-${subsection.id}-totalClaimedQuantity`}
                                value={subsection.totalClaimedQuantity}
                                onChange={(value) =>
                                  handleFieldEdit(
                                    "totalClaimedQuantity",
                                    value,
                                    section.id,
                                    subsection.id
                                  )
                                }
                                isNumeric={true}
                              />
                            </div>
                            <div className="info-item">
                              <label>Total Approved Quantity</label>
                              <EditableField
                                id={`${section.id}-${subsection.id}-totalApprovedQuantity`}
                                value={subsection.totalApprovedQuantity}
                                onChange={(value) =>
                                  handleFieldEdit(
                                    "totalApprovedQuantity",
                                    value,
                                    section.id,
                                    subsection.id
                                  )
                                }
                                isNumeric={true}
                              />
                            </div>
                            <div className="info-item">
                              <label>Units</label>
                              <EditableField
                                id={`${section.id}-${subsection.id}-units`}
                                value={subsection.units}
                                onChange={(value) =>
                                  handleFieldEdit(
                                    "units",
                                    value,
                                    section.id,
                                    subsection.id
                                  )
                                }
                              />
                            </div>
                            <div className="info-item total-amount">
                              <label>Total Amount</label>
                              <EditableField
                                id={`${section.id}-${subsection.id}-totalAmount`}
                                value={subsection.totalAmount}
                                onChange={(value) =>
                                  handleFieldEdit(
                                    "totalAmount",
                                    value,
                                    section.id,
                                    subsection.id
                                  )
                                }
                                prefix="₹ "
                                isNumeric={true}
                                className="amount-field"
                              />
                            </div>
                            {!readOnly && (
                              <div>
                                <button
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#FF8282",
                                  }}
                                  onClick={(e) => {
                                    console.log("Delete subsection");
                                    e.stopPropagation();
                                    if (
                                      window.confirm(
                                        "Are you sure you want to delete this subsection?"
                                      )
                                    ) {
                                      deleteSubsection(
                                        section.id,
                                        subsection.id
                                      );
                                    }
                                  }}
                                  title="Delete Subsection"
                                >
                                  <FiTrash2
                                    style={{
                                      color: "#FF8282",
                                      fontSize: "20px",
                                      cursor: "pointer",
                                    }}
                                  />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {subsection.isExpanded && (
                          <div className="subsection-content">
                            <div className="measurement-header">
                              <h3>Measurement sheet :</h3>
                            </div>

                            {subsection.tables &&
                              subsection.tables.map((table) => (
                                <div
                                  key={table.id}
                                  className="measurement-table-section"
                                >
                                  <div className="table-title">
                                    <EditableField
                                      id={`${section.id}-${subsection.id}-${table.id}-title`}
                                      value={
                                        table.tableTitle ||
                                        table.title ||
                                        "New Table"
                                      }
                                      onChange={(value) => {
                                        handleFieldEdit(
                                          "tableTitle",
                                          value,
                                          section.id,
                                          subsection.id,
                                          table.id
                                        );
                                      }}
                                      className="table-title-field"
                                    />
                                  </div>

                                  <div className="excel-table">
                                    <div className="table-header">
                                      <div className="cell s-no">S.No</div>
                                      <div className="cell description">
                                        Description
                                      </div>
                                      <div className="cell length">Length</div>
                                      <div className="cell breadth">
                                        Breadth
                                      </div>
                                      <div className="cell height">Height</div>
                                      <div className="cell units">Units</div>
                                      <div className="cell nos">Nos</div>
                                      <div className="cell total-quantity">
                                        Total Quantity
                                      </div>
                                      <div className="cell rate">Rate (₹)</div>
                                      <div className="cell rate">GST (%)</div>
                                      <div className="cell amount">
                                        Amount (₹)
                                      </div>
                                      <div className="cell actions">
                                        Actions
                                      </div>
                                    </div>
                                    <DragDropContext
                                      onDragEnd={(result) =>
                                        handleDragEnd(
                                          result,
                                          section.id,
                                          subsection.id,
                                          table.id
                                        )
                                      }
                                    >
                                      <Droppable
                                        droppableId={`table-${table.id}`}
                                      >
                                        {(provided) => (
                                          <div
                                            className="table-body"
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                          >
                                            {getSortedRowsBySNo(table.rows).map(
                                              (row, index) => (
                                                <Draggable
                                                  key={row.id}
                                                  draggableId={row.id}
                                                  index={index}
                                                  isDragDisabled={
                                                    isDraggingDisabled ||
                                                    readOnly ||
                                                    row.isPrevious
                                                  } // Disable dragging for previous items
                                                >
                                                  {(provided) => (
                                                    <div
                                                      ref={provided.innerRef}
                                                      {...provided.draggableProps}
                                                      {...provided.dragHandleProps}
                                                      onClick={(e) => {
                                                        // Only allow drag if not clicking on an editable field
                                                        if (
                                                          e.target.closest(
                                                            ".editable-field"
                                                          )
                                                        ) {
                                                          e.stopPropagation();
                                                        }
                                                      }}
                                                      className={
                                                        row.isPrevious
                                                          ? "previous-measurement-row"
                                                          : ""
                                                      }
                                                    >
                                                      <div className="table-row">
                                                        <div className="cell s-no">
                                                          {row.sNo}
                                                        </div>
                                                        <div className="cell description">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-description`}
                                                            value={
                                                              row.description
                                                            }
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "description",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell length">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-length`}
                                                            value={row.length}
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "length",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isNumeric={true}
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell breadth">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-breadth`}
                                                            value={row.breadth}
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "breadth",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isNumeric={true}
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell height">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-height`}
                                                            value={row.height}
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "height",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isNumeric={true}
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell units">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-units`}
                                                            value={row.units}
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "units",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell nos">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-nos`}
                                                            value={row.nos}
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "nos",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isNumeric={true}
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell total-quantity">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-totalQuantity`}
                                                            value={
                                                              row.totalQuantity
                                                            }
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "totalQuantity",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isNumeric={true}
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell rate">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-rate`}
                                                            value={row.rate}
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "rate",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isNumeric={true}
                                                            prefix="₹ "
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell rate">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-gst`}
                                                            value={row.gst}
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "gst",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isNumeric={true}
                                                            suffix="%"
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell amount">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-amount`}
                                                            value={row.amount}
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "amount",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isNumeric={true}
                                                            prefix="₹ "
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div
                                                          className="cell actions"
                                                          style={{
                                                            display: "flex",
                                                            justifyContent:
                                                              "space-around",
                                                          }}
                                                        >
                                                          {!readOnly &&
                                                            !row.isPrevious && (
                                                              <>
                                                                <button
                                                                  style={{
                                                                    background:
                                                                      "transparent",
                                                                    border:
                                                                      "none",
                                                                    cursor:
                                                                      "pointer",
                                                                    color:
                                                                      "#FF8282",
                                                                  }}
                                                                  onClick={() =>
                                                                    addDeduction(
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id
                                                                    )
                                                                  }
                                                                  title="Add Deduction"
                                                                >
                                                                  <FiMinus
                                                                    style={{
                                                                      color:
                                                                        "#FF8282",
                                                                      fontSize:
                                                                        "16px",
                                                                      cursor:
                                                                        "pointer",
                                                                    }}
                                                                  />
                                                                </button>
                                                                <button
                                                                  style={{
                                                                    background:
                                                                      "transparent",
                                                                    border:
                                                                      "none",
                                                                    cursor:
                                                                      "pointer",
                                                                    color:
                                                                      "#FF8282",
                                                                  }}
                                                                  onClick={() =>
                                                                    deleteItem(
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id
                                                                    )
                                                                  }
                                                                  title="Delete Row"
                                                                >
                                                                  <FiTrash2
                                                                    style={{
                                                                      color:
                                                                        "#FF8282",
                                                                      fontSize:
                                                                        "16px",
                                                                      cursor:
                                                                        "pointer",
                                                                    }}
                                                                  />
                                                                </button>
                                                              </>
                                                            )}
                                                          {row.isPrevious && (
                                                            <span className="previous-label">
                                                              Previous Bill
                                                            </span>
                                                          )}
                                                        </div>
                                                      </div>

                                                      {/* Deductions */}
                                                      {row.deductions &&
                                                        row.deductions.map(
                                                          (deduction) => (
                                                            <div
                                                              className="table-row deduction-row"
                                                              key={deduction.id}
                                                            >
                                                              <div className="cell s-no"></div>
                                                              <div className="cell description">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-description`}
                                                                  value={
                                                                    deduction.description
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "description",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell length">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-length`}
                                                                  value={
                                                                    deduction.length
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "length",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isNumeric={
                                                                    true
                                                                  }
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell breadth">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-breadth`}
                                                                  value={
                                                                    deduction.breadth
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "breadth",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isNumeric={
                                                                    true
                                                                  }
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell height">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-height`}
                                                                  value={
                                                                    deduction.height
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "height",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isNumeric={
                                                                    true
                                                                  }
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell units">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-units`}
                                                                  value={
                                                                    deduction.units
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "units",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell nos">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-nos`}
                                                                  value={
                                                                    deduction.nos
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "nos",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isNumeric={
                                                                    true
                                                                  }
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell total-quantity">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-totalQuantity`}
                                                                  value={
                                                                    deduction.totalQuantity
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "totalQuantity",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isNumeric={
                                                                    true
                                                                  }
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell rate">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-rate`}
                                                                  value={
                                                                    deduction.rate
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "rate",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isNumeric={
                                                                    true
                                                                  }
                                                                  prefix="₹ "
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell rate">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-gst`}
                                                                  value={
                                                                    deduction.gst
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "gst",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isNumeric={
                                                                    true
                                                                  }
                                                                  prefix="₹ "
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell amount deduction-amount">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-amount`}
                                                                  value={
                                                                    deduction.amount
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "amount",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isNumeric={
                                                                    true
                                                                  }
                                                                  prefix="₹ -"
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div
                                                                className="cell actions"
                                                                style={{
                                                                  display:
                                                                    "flex",
                                                                  justifyContent:
                                                                    "space-around",
                                                                }}
                                                              >
                                                                {!readOnly &&
                                                                  !row.isPrevious && (
                                                                    <button
                                                                      style={{
                                                                        background:
                                                                          "transparent",
                                                                        border:
                                                                          "none",
                                                                        cursor:
                                                                          "pointer",
                                                                        color:
                                                                          "#FF8282",
                                                                      }}
                                                                      onClick={() =>
                                                                        deleteItem(
                                                                          section.id,
                                                                          subsection.id,
                                                                          table.id,
                                                                          row.id,
                                                                          deduction.id
                                                                        )
                                                                      }
                                                                      title="Delete Deduction"
                                                                    >
                                                                      <FiTrash2
                                                                        style={{
                                                                          color:
                                                                            "#FF8282",
                                                                          fontSize:
                                                                            "16px",
                                                                          cursor:
                                                                            "pointer",
                                                                        }}
                                                                      />
                                                                    </button>
                                                                  )}
                                                                {row.isPrevious && (
                                                                  <span className="previous-label">
                                                                    Previous
                                                                    Bill
                                                                  </span>
                                                                )}
                                                              </div>
                                                            </div>
                                                          )
                                                        )}
                                                    </div>
                                                  )}
                                                </Draggable>
                                              )
                                            )}
                                            {provided.placeholder}
                                          </div>
                                        )}
                                      </Droppable>
                                    </DragDropContext>

                                    <div className="table-footer">
                                      {!readOnly && (
                                        <button
                                          className="add-row-btn"
                                          onClick={() =>
                                            addRow(
                                              section.id,
                                              subsection.id,
                                              table.id
                                            )
                                          }
                                        >
                                          <FiPlus /> Add Row
                                        </button>
                                      )}

                                      <div className="table-totals">
                                        <div className="total-item">
                                          PREVIOUS BILL: ₹{" "}
                                          {formatCurrency(
                                            calculateTableTotals(
                                              section.id,
                                              subsection.id,
                                              table.id
                                            ).previousTotal
                                          )}
                                        </div>
                                        <div className="total-item">
                                          PRESENT BILL: ₹{" "}
                                          {formatCurrency(
                                            calculateTableTotals(
                                              section.id,
                                              subsection.id,
                                              table.id
                                            ).presentTotal
                                          )}
                                        </div>
                                        <div className="total-item">
                                          TOTAL AMOUNT: ₹{" "}
                                          {formatCurrency(
                                            calculateTableTotals(
                                              section.id,
                                              subsection.id,
                                              table.id
                                            ).total
                                          )}
                                        </div>
                                        <div className="total-item">
                                          DEDUCTION: ₹ -
                                          {formatCurrency(
                                            calculateTableTotals(
                                              section.id,
                                              subsection.id,
                                              table.id
                                            ).deductions
                                          )}
                                        </div>
                                        <div className="total-item">
                                          NET TOTAL: ₹{" "}
                                          {formatCurrency(
                                            calculateTableTotals(
                                              section.id,
                                              subsection.id,
                                              table.id
                                            ).net
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}

                            {!readOnly && (
                              <div className="add-table-btn-container">
                                <button
                                  className="add-table-btn"
                                  onClick={() =>
                                    addTable(section.id, subsection.id)
                                  }
                                >
                                  <FiPlus /> Add Table
                                </button>
                              </div>
                            )}
                            <ImageGrid
                              images={workOrderImages}
                              onDeleteImage={handleDeleteImageForWorkOrder}
                            />
                            {!readOnly && (
                              <MultipleImageUploader
                                onUploadComplete={
                                  handleUploadCompleteForWorkOrder
                                }
                                basePath="product-images" // Optional: customize storage path
                              />
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="empty-section">
                      <p>
                        No work order items available.
                        {!readOnly && "Add a subsection to get started."}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {section.isExpanded && section.id === "additionalItems" && (
                <div className="section-content">
                  {section.subsections && section.subsections.length > 0 ? (
                    section.subsections.map((subsection) => (
                      <div key={subsection.id} className="subsection">
                        <div
                          className="subsection-header"
                          onClick={() =>
                            toggleSubsection(section.id, subsection.id)
                          }
                        >
                          <div className="subsection-title">
                            <EditableField
                              id={`${section.id}-${subsection.id}-title`}
                              value={subsection.title}
                              onChange={(value) =>
                                handleFieldEdit(
                                  "title",
                                  value,
                                  section.id,
                                  subsection.id
                                )
                              }
                            />
                            <span className="subtitle">
                              <EditableField
                                id={`${section.id}-${subsection.id}-subtitle`}
                                value={subsection.subtitle}
                                onChange={(value) =>
                                  handleFieldEdit(
                                    "subtitle",
                                    value,
                                    section.id,
                                    subsection.id
                                  )
                                }
                              />
                            </span>
                          </div>
                          <div className="subsection-info">
                            <div className="info-item">
                              <label>PO Quantity</label>
                              <EditableField
                                id={`${section.id}-${subsection.id}-poQuantity`}
                                value={subsection.poQuantity}
                                onChange={(value) =>
                                  handleFieldEdit(
                                    "poQuantity",
                                    value,
                                    section.id,
                                    subsection.id
                                  )
                                }
                                isNumeric={true}
                              />
                            </div>
                            <div className="info-item">
                              <label>Total Claimed Quantity</label>
                              <EditableField
                                id={`${section.id}-${subsection.id}-totalClaimedQuantity`}
                                value={subsection.totalClaimedQuantity}
                                onChange={(value) =>
                                  handleFieldEdit(
                                    "totalClaimedQuantity",
                                    value,
                                    section.id,
                                    subsection.id
                                  )
                                }
                                isNumeric={true}
                              />
                            </div>
                            <div className="info-item">
                              <label>Total Approved Quantity</label>
                              <EditableField
                                id={`${section.id}-${subsection.id}-totalApprovedQuantity`}
                                value={subsection.totalApprovedQuantity}
                                onChange={(value) =>
                                  handleFieldEdit(
                                    "totalApprovedQuantity",
                                    value,
                                    section.id,
                                    subsection.id
                                  )
                                }
                                isNumeric={true}
                              />
                            </div>
                            <div className="info-item">
                              <label>Units</label>
                              <EditableField
                                id={`${section.id}-${subsection.id}-units`}
                                value={subsection.units}
                                onChange={(value) =>
                                  handleFieldEdit(
                                    "units",
                                    value,
                                    section.id,
                                    subsection.id
                                  )
                                }
                              />
                            </div>
                            <div className="info-item total-amount">
                              <label>Total Amount</label>
                              <EditableField
                                id={`${section.id}-${subsection.id}-totalAmount`}
                                value={subsection.totalAmount}
                                onChange={(value) =>
                                  handleFieldEdit(
                                    "totalAmount",
                                    value,
                                    section.id,
                                    subsection.id
                                  )
                                }
                                prefix="₹ "
                                isNumeric={true}
                                className="amount-field"
                              />
                            </div>
                          </div>
                        </div>

                        {subsection.isExpanded && (
                          <div className="subsection-content">
                            <div className="measurement-header">
                              <h3>Measurement sheet :</h3>
                            </div>

                            {subsection.tables &&
                              subsection.tables.map((table) => (
                                <div
                                  key={table.id}
                                  className="measurement-table-section"
                                >
                                  <div className="table-title">
                                    <EditableField
                                      id={`${section.id}-${subsection.id}-${table.id}-title`}
                                      value={
                                        table.tableTitle ||
                                        table.title ||
                                        "New Table"
                                      }
                                      onChange={(value) => {
                                        handleFieldEdit(
                                          "tableTitle",
                                          value,
                                          section.id,
                                          subsection.id,
                                          table.id
                                        );
                                      }}
                                      className="table-title-field"
                                    />
                                  </div>

                                  <div className="excel-table">
                                    <div className="table-header">
                                      <div className="cell s-no">S.No</div>
                                      <div className="cell description">
                                        Description
                                      </div>
                                      <div className="cell length">Length</div>
                                      <div className="cell breadth">
                                        Breadth
                                      </div>
                                      <div className="cell height">Height</div>
                                      <div className="cell units">Units</div>
                                      <div className="cell nos">Nos</div>
                                      <div className="cell total-quantity">
                                        Total Quantity
                                      </div>
                                      <div className="cell rate">Rate (₹)</div>
                                      <div className="cell rate">GST (%)</div>

                                      <div className="cell amount">
                                        Amount (₹)
                                      </div>
                                      <div className="cell actions">
                                        Actions
                                      </div>
                                    </div>

                                    <DragDropContext
                                      onDragEnd={(result) =>
                                        handleDragEnd(
                                          result,
                                          section.id,
                                          subsection.id,
                                          table.id
                                        )
                                      }
                                    >
                                      <Droppable
                                        droppableId={`table-${table.id}`}
                                      >
                                        {(provided) => (
                                          <div
                                            className="table-body"
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                          >
                                            {getSortedRowsBySNo(table.rows).map(
                                              (row, index) => (
                                                <Draggable
                                                  key={row.id}
                                                  draggableId={row.id}
                                                  index={index}
                                                  isDragDisabled={
                                                    isDraggingDisabled ||
                                                    readOnly ||
                                                    row.isPrevious
                                                  } // Disable dragging for previous items
                                                >
                                                  {(provided) => (
                                                    <div
                                                      ref={provided.innerRef}
                                                      {...provided.draggableProps}
                                                      {...provided.dragHandleProps}
                                                      onClick={(e) => {
                                                        if (
                                                          e.target.closest(
                                                            ".editable-field"
                                                          )
                                                        ) {
                                                          e.stopPropagation();
                                                        }
                                                      }}
                                                      className={
                                                        row.isPrevious
                                                          ? "previous-measurement-row"
                                                          : ""
                                                      }
                                                    >
                                                      {/* Same row structure as workOrderItems section */}
                                                      <div className="table-row">
                                                        <div className="cell s-no">
                                                          {row.sNo}
                                                        </div>
                                                        <div className="cell description">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-description`}
                                                            value={
                                                              row.description
                                                            }
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "description",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell length">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-length`}
                                                            value={row.length}
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "length",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isNumeric={true}
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell breadth">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-breadth`}
                                                            value={row.breadth}
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "breadth",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isNumeric={true}
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell height">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-height`}
                                                            value={row.height}
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "height",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isNumeric={true}
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell units">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-units`}
                                                            value={row.units}
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "units",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell nos">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-nos`}
                                                            value={row.nos}
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "nos",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isNumeric={true}
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell total-quantity">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-totalQuantity`}
                                                            value={
                                                              row.totalQuantity
                                                            }
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "totalQuantity",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isNumeric={true}
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell rate">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-rate`}
                                                            value={row.rate}
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "rate",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isNumeric={true}
                                                            prefix="₹ "
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell rate">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-gst`}
                                                            value={row.gst}
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "gst",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isNumeric={true}
                                                            suffix="%"
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div className="cell amount">
                                                          <EditableField
                                                            id={`${section.id}-${subsection.id}-${table.id}-${row.id}-amount`}
                                                            value={row.amount}
                                                            onChange={(value) =>
                                                              handleFieldEdit(
                                                                "amount",
                                                                value,
                                                                section.id,
                                                                subsection.id,
                                                                table.id,
                                                                row.id
                                                              )
                                                            }
                                                            isNumeric={true}
                                                            prefix="₹ "
                                                            isPrevious={
                                                              row.isPrevious
                                                            }
                                                          />
                                                        </div>
                                                        <div
                                                          className="cell actions"
                                                          style={{
                                                            display: "flex",
                                                            justifyContent:
                                                              "space-around",
                                                          }}
                                                        >
                                                          {!readOnly &&
                                                            !row.isPrevious && (
                                                              <>
                                                                <button
                                                                  style={{
                                                                    background:
                                                                      "transparent",
                                                                    border:
                                                                      "none",
                                                                    cursor:
                                                                      "pointer",
                                                                    color:
                                                                      "#FF8282",
                                                                  }}
                                                                  onClick={() =>
                                                                    addDeduction(
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id
                                                                    )
                                                                  }
                                                                  title="Add Deduction"
                                                                >
                                                                  <FiMinus
                                                                    style={{
                                                                      color:
                                                                        "#FF8282",
                                                                      fontSize:
                                                                        "16px",
                                                                      cursor:
                                                                        "pointer",
                                                                    }}
                                                                  />
                                                                </button>
                                                                <button
                                                                  style={{
                                                                    background:
                                                                      "transparent",
                                                                    border:
                                                                      "none",
                                                                    cursor:
                                                                      "pointer",
                                                                    color:
                                                                      "#FF8282",
                                                                  }}
                                                                  onClick={() =>
                                                                    deleteItem(
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id
                                                                    )
                                                                  }
                                                                  title="Delete Row"
                                                                >
                                                                  <FiTrash2
                                                                    style={{
                                                                      color:
                                                                        "#FF8282",
                                                                      fontSize:
                                                                        "16px",
                                                                      cursor:
                                                                        "pointer",
                                                                    }}
                                                                  />
                                                                </button>
                                                              </>
                                                            )}
                                                          {row.isPrevious && (
                                                            <span className="previous-label">
                                                              Previous Bill
                                                            </span>
                                                          )}
                                                        </div>
                                                      </div>

                                                      {/* Deductions rows - same as in workOrderItems */}
                                                      {row.deductions &&
                                                        row.deductions.map(
                                                          (deduction) => (
                                                            <div
                                                              className="table-row deduction-row"
                                                              key={deduction.id}
                                                            >
                                                              {/* Same deduction structure as workOrderItems section */}
                                                              <div className="cell s-no"></div>
                                                              <div className="cell description">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-description`}
                                                                  value={
                                                                    deduction.description
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "description",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell length">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-length`}
                                                                  value={
                                                                    deduction.length
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "length",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isNumeric={
                                                                    true
                                                                  }
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell breadth">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-breadth`}
                                                                  value={
                                                                    deduction.breadth
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "breadth",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isNumeric={
                                                                    true
                                                                  }
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell height">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-height`}
                                                                  value={
                                                                    deduction.height
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "height",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isNumeric={
                                                                    true
                                                                  }
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell units">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-units`}
                                                                  value={
                                                                    deduction.units
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "units",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell nos">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-nos`}
                                                                  value={
                                                                    deduction.nos
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "nos",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isNumeric={
                                                                    true
                                                                  }
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell total-quantity">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-totalQuantity`}
                                                                  value={
                                                                    deduction.totalQuantity
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "totalQuantity",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isNumeric={
                                                                    true
                                                                  }
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell rate">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-rate`}
                                                                  value={
                                                                    deduction.rate
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "rate",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isNumeric={
                                                                    true
                                                                  }
                                                                  prefix="₹ "
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell rate">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-gst`}
                                                                  value={
                                                                    deduction.gst
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "gst",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isNumeric={
                                                                    true
                                                                  }
                                                                  prefix="₹ "
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="cell amount deduction-amount">
                                                                <EditableField
                                                                  id={`${section.id}-${subsection.id}-${table.id}-${row.id}-${deduction.id}-amount`}
                                                                  value={
                                                                    deduction.amount
                                                                  }
                                                                  onChange={(
                                                                    value
                                                                  ) =>
                                                                    handleFieldEdit(
                                                                      "amount",
                                                                      value,
                                                                      section.id,
                                                                      subsection.id,
                                                                      table.id,
                                                                      row.id,
                                                                      deduction.id
                                                                    )
                                                                  }
                                                                  isNumeric={
                                                                    true
                                                                  }
                                                                  prefix="₹ -"
                                                                  isPrevious={
                                                                    row.isPrevious
                                                                  }
                                                                />
                                                              </div>
                                                              <div
                                                                className="cell actions"
                                                                style={{
                                                                  display:
                                                                    "flex",
                                                                  justifyContent:
                                                                    "space-around",
                                                                }}
                                                              >
                                                                {!readOnly &&
                                                                  !row.isPrevious && (
                                                                    <button
                                                                      style={{
                                                                        background:
                                                                          "transparent",
                                                                        border:
                                                                          "none",
                                                                        cursor:
                                                                          "pointer",
                                                                        color:
                                                                          "#FF8282",
                                                                      }}
                                                                      onClick={() =>
                                                                        deleteItem(
                                                                          section.id,
                                                                          subsection.id,
                                                                          table.id,
                                                                          row.id,
                                                                          deduction.id
                                                                        )
                                                                      }
                                                                      title="Delete Deduction"
                                                                    >
                                                                      <FiTrash2
                                                                        style={{
                                                                          color:
                                                                            "#FF8282",
                                                                          fontSize:
                                                                            "16px",
                                                                          cursor:
                                                                            "pointer",
                                                                        }}
                                                                      />
                                                                    </button>
                                                                  )}
                                                                {row.isPrevious && (
                                                                  <span className="previous-label">
                                                                    Previous
                                                                    Bill
                                                                  </span>
                                                                )}
                                                              </div>
                                                            </div>
                                                          )
                                                        )}
                                                    </div>
                                                  )}
                                                </Draggable>
                                              )
                                            )}
                                            {provided.placeholder}
                                          </div>
                                        )}
                                      </Droppable>
                                    </DragDropContext>

                                    <div className="table-footer">
                                      {!readOnly && (
                                        <button
                                          className="add-row-btn"
                                          onClick={() =>
                                            addRow(
                                              section.id,
                                              subsection.id,
                                              table.id
                                            )
                                          }
                                        >
                                          <FiPlus /> Add Row
                                        </button>
                                      )}

                                      <div className="table-totals">
                                        <div className="total-item">
                                          TOTAL AMOUNT: ₹{" "}
                                          {formatCurrency(
                                            calculateTableTotals(
                                              section.id,
                                              subsection.id,
                                              table.id
                                            ).total
                                          )}
                                        </div>
                                        <div className="total-item">
                                          DEDUCTION: ₹ -
                                          {formatCurrency(
                                            calculateTableTotals(
                                              section.id,
                                              subsection.id,
                                              table.id
                                            ).deductions
                                          )}
                                        </div>
                                        <div className="total-item">
                                          NET TOTAL: ₹{" "}
                                          {formatCurrency(
                                            calculateTableTotals(
                                              section.id,
                                              subsection.id,
                                              table.id
                                            ).net
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}

                            {!readOnly && (
                              <div className="add-table-btn-container">
                                <button
                                  className="add-table-btn"
                                  onClick={() =>
                                    addTable(section.id, subsection.id)
                                  }
                                >
                                  <FiPlus /> Add Table
                                </button>
                              </div>
                            )}
                            <ImageGrid
                              images={additionalImages}
                              onDeleteImage={handleDeleteImageForAdditional}
                            />
                            {!readOnly && (
                              <MultipleImageUploader
                                onUploadComplete={
                                  handleUploadCompleteForAdditional
                                }
                                basePath="product-images" // Optional: customize storage path
                              />
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="empty-section">
                      <p>
                        No additional items available.
                        {!readOnly && " Add a subsection to get started."}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          <PaymentMilestones
            milestones={JSON.parse(
              billData.serviceOrder.serviceOrder.CommonMilestones
            )}
          />
          {/* Bill Summary Section */}
          <div className="bill-summary-container">
            {" "}
            <div className="total-bill-summary">
              <h3>TOTAL BILL SUMMARY</h3>
              <div className="bill-view-options">
                <div className="tab-container">
                  <button
                    className={`tab-button ${!showTotalView ? "active" : ""}`}
                    onClick={() => setShowTotalView(false)}
                  >
                    <FiMenu size={14} />
                    Bills List
                  </button>
                  <button
                    className={`tab-button ${showTotalView ? "active" : ""}`}
                    onClick={() => {
                      setShowTotalView(true);
                      // Update the present bill in the summary based on billedAmount
                      setBillSummary((prev) => ({
                        ...prev,
                        presentBill: parseFloat(billedAmount) || 0,
                        totalAmount:
                          (prev.previousBill || 0) +
                          (parseFloat(billedAmount) || 0),
                      }));
                    }}
                  >
                    <FiCheckCircle size={14} />
                    Summary View
                  </button>
                </div>
              </div>
              <div className="bill-history-table">
                {!showTotalView ? (
                  <>
                    <div className="bill-history-header">
                      <div></div>
                      <div>PAID</div>
                      <div>DUE</div>
                      <div>REQUESTED</div>

                      <div></div>
                    </div>
                    <div className="bill-history-body">
                      {relatedBills.length > 0 ? (
                        <>
                          {relatedBills.map((bill, index) => (
                            <div
                              key={bill.billId}
                              className={`bill-history-row ${
                                bill.billId === billId ? "active-bill" : ""
                              }`}
                            >
                              <div>{bill.billNo}</div>
                              <div>
                                ₹{" "}
                                {formatCurrency(
                                  bill.status === "Paid"
                                    ? bill.paidAmount
                                      ? bill.paidAmount
                                      : bill.presentBill
                                    : 0
                                )}
                              </div>
                              <div>
                                ₹{" "}
                                {formatCurrency(
                                  bill.paidAmount
                                    ? bill.presentBill - bill.paidAmount
                                    : bill.presentBill
                                )}
                              </div>
                              <div>₹ {formatCurrency(bill.presentBill)}</div>
                              {/* <div>
                                <button
                                  className="view-doc-btn"
                                  onClick={() => handleBillSelection(index)}
                                  title={
                                    bill.billId === billId
                                      ? "Current bill"
                                      : "View this bill"
                                  }
                                  disabled={bill.billId === billId}
                                >
                                  <FiFileText size={16} />
                                </button>
                              </div> */}
                            </div>
                          ))}
                          <div
                            className="bill-history-row view-more"
                            onClick={handleViewMoreBills}
                            style={{
                              cursor: "pointer",
                              color: "#007bff",
                              fontWeight: "bold",
                              marginLeft: "5px",
                              marginRight: "5px",
                              display: "flex",
                              alignItems: "flex-end",
                              justifyContent: "flex-end",
                              padding: "10px 10px",
                            }}
                          >
                            View All
                          </div>
                        </>
                      ) : (
                        <div className="bill-history-row no-bills">
                          <div>No other bills found for this PO</div>
                          <div></div>
                          <div></div>
                          <div></div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="bill-total-summary">
                    <div className="total-summary-item">
                      <div className="total-label">Total Bills</div>
                      <div className="total-value">{relatedBills.length}</div>
                    </div>
                    <div className="total-summary-item">
                      <div className="total-label">Total Amount</div>
                      <div className="total-value">
                        ₹ {formatCurrency(billsSummary.totalAmount)}
                      </div>
                    </div>
                    <div className="total-summary-item">
                      <div className="total-label">Paid Amount</div>
                      <div className="total-value">
                        ₹ {formatCurrency(billsSummary.totalPaid)}
                      </div>
                    </div>
                    <div className="total-summary-item">
                      <div className="total-label">Due Amount</div>
                      <div className="total-value">
                        ₹ {formatCurrency(billsSummary.totalDue)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="bill-summary">
              <h3>BILL {currentBillIndex + 1} SUMMARY</h3>
              <div className="summary-content">
                <div className="summary-row">
                  <div className="summary-label">Previous Bill</div>
                  <div className="summary-value">
                    <EditableField
                      id="summary.previousBill"
                      value={billSummary.previousBill}
                      onChange={(value) =>
                        handleFieldEdit(
                          "summary.previousBill",
                          parseFloat(value) || 0
                        )
                      }
                      isNumeric={true}
                      prefix="₹ "
                    />
                  </div>
                </div>{" "}
                <div className="summary-row">
                  <div className="summary-label">Present Bill</div>
                  <div className="summary-value">
                    <EditableField
                      id="summary.presentBill"
                      value={billSummary.presentBill || billedAmount}
                      onChange={(value) =>
                        handleFieldEdit(
                          "summary.presentBill",
                          parseFloat(value) || 0
                        )
                      }
                      isNumeric={true}
                      prefix="₹ "
                    />
                  </div>
                </div>
                <div className="summary-row">
                  <div className="summary-label">Total Amount</div>
                  <div className="summary-value">
                    ₹ {formatCurrency(billSummary.totalAmount)}
                  </div>
                </div>
                <div className="summary-row">
                  <div className="summary-label">Retention Money (%)</div>
                  <div className="summary-value">
                    <EditableField
                      id="summary.retentionPercentage"
                      value={billSummary.retentionPercentage}
                      onChange={(value) =>
                        handleFieldEdit(
                          "summary.retentionPercentage",
                          parseFloat(value) || 0
                        )
                      }
                      isNumeric={true}
                      suffix=" %"
                    />
                  </div>
                </div>
                <div className="summary-row">
                  <div className="summary-label">Retention Money</div>
                  <div className="summary-value">
                    ₹ {formatCurrency(billSummary.retentionMoney)}
                  </div>
                </div>{" "}
                {/* <div className="summary-row">
                  <div className="summary-label">Previous Total Amount</div>
                  <div className="summary-value">
                    <EditableField
                      id="summary.previousTotalAmount"
                      value={
                        billSummary.previousTotalAmount ||
                        billSummary.previousBill
                      }
                      onChange={(value) => {
                        // When previousTotalAmount changes, update previousBill too for consistency
                        handleFieldEdit(
                          "summary.previousTotalAmount",
                          parseFloat(value) || 0
                        );
                        setBillSummary((prev) => ({
                          ...prev,
                          previousBill: parseFloat(value) || 0,
                          totalAmount:
                            (parseFloat(value) || 0) + prev.presentBill,
                        }));
                      }}
                      isNumeric={true}
                      prefix="₹ "
                    />
                  </div>
                </div> */}
                <div className="summary-row">
                  <div className="summary-label">Already Paid Amount</div>
                  <div className="summary-value">
                    <EditableField
                      id="summary.alreadyPaidAmount"
                      value={totalPaidAmount}
                      onChange={(value) => {
                        setTotalPaidAmount(parseFloat(value) || 0);
                      }}
                      isNumeric={true}
                      prefix="₹ "
                    />
                  </div>
                </div>
                <div className="summary-row">
                  <div className="summary-label">Approved Amount</div>
                  <div className="summary-value">
                    <EditableField
                      id="billedAmount"
                      value={billedAmount}
                      onChange={(value) => {
                        handleFieldEdit("billedAmount", value);
                        // Update present bill when billedAmount changes
                        setBillSummary((prev) => ({
                          ...prev,
                          presentBill: parseFloat(value) || 0,
                          totalAmount:
                            (prev.previousBill || 0) + (parseFloat(value) || 0),
                        }));
                      }}
                      isNumeric={true}
                      prefix="₹ "
                    />
                  </div>
                </div>
                <div className="summary-row net-payable">
                  <div className="summary-label">NET PAYABLE</div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "bold",
                      color: "red",
                    }}
                  >
                    ₹ {formatCurrency(billSummary.netPayable)}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Final Remarks */}
          <div className="final-remarks">
            <div className="remark-field">
              <label>Final Remark</label>
              <EditableTextArea
                id="finalRemark"
                value={finalRemark}
                onChange={(value) => handleFieldEdit("finalRemark", value)}
                className="remark-textarea"
              />
            </div>

            <div className="approvals">
              <div className="approval-step">
                <div className="step-label">Raised by</div>
                <div className="step-name">
                  <EditableField
                    id="approval.raisedBy"
                    value={approvalDetails.raisedBy}
                    onChange={(value) => {
                      setApprovalDetails((prev) => ({
                        ...prev,
                        raisedBy: value,
                      }));
                    }}
                  />
                </div>
                <div className="step-date">
                  <EditableField
                    id="approval.raisedDate"
                    value={approvalDetails.raisedDate}
                    onChange={(value) => {
                      setApprovalDetails((prev) => ({
                        ...prev,
                        raisedDate: value,
                      }));
                    }}
                  />
                </div>
              </div>

              <div className="approval-step">
                <div className="step-label">Checked by</div>
                <div className="step-name">
                  <EditableField
                    id="approval.checkedBy"
                    value={approvalDetails.checkedBy}
                    onChange={(value) => {
                      setApprovalDetails((prev) => ({
                        ...prev,
                        checkedBy: value,
                      }));
                    }}
                  />
                </div>
                <div className="step-date">
                  <EditableField
                    id="approval.checkedDate"
                    value={approvalDetails.checkedDate}
                    onChange={(value) => {
                      setApprovalDetails((prev) => ({
                        ...prev,
                        checkedDate: value,
                      }));
                    }}
                  />
                </div>
              </div>

              <div className="approval-step">
                <div className="step-label">Approved by</div>
                <div className="step-name">
                  <EditableField
                    id="approval.approvedBy"
                    value={approvalDetails.approvedBy}
                    onChange={(value) => {
                      setApprovalDetails((prev) => ({
                        ...prev,
                        approvedBy: value,
                      }));
                    }}
                  />
                </div>
                <div className="step-date">
                  <EditableField
                    id="approval.approvedDate"
                    value={approvalDetails.approvedDate}
                    onChange={(value) => {
                      setApprovalDetails((prev) => ({
                        ...prev,
                        approvedDate: value,
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading indicator */}
      <ProgressIndicator
        isLoading={isSubmitting}
        progress={loadingProgress}
        message={loadingMessage}
      />

      {showAllBills && (
        <div className="all-bills-modal-overlay" onClick={closeAllBillsModal}>
          <div className="all-bills-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <FiMenu className="header-icon" />
                All Bills for PO #{poId || "N/A"}
              </h3>
              <button className="close-btn" onClick={closeAllBillsModal}>
                <FiX size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-stats">
                <div className="stat-item">
                  <div className="stat-value">{relatedBills.length}</div>
                  <div className="stat-label">Total Bills</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">
                    ₹ {formatCurrency(billsSummary.totalAmount)}
                  </div>
                  <div className="stat-label">Total Amount</div>
                </div>
                <div className="stat-item paid">
                  <div className="stat-value">
                    ₹ {formatCurrency(billsSummary.totalPaid)}
                  </div>
                  <div className="stat-label">Paid Amount</div>
                </div>
                <div className="stat-item due">
                  <div className="stat-value">
                    ₹ {formatCurrency(billsSummary.totalDue)}
                  </div>
                  <div className="stat-label">Due Amount</div>
                </div>
              </div>
              <table className="bills-table">
                <thead>
                  <tr>
                    <th>Bill #</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    {/* <th>Actions</th> */}
                  </tr>
                </thead>
                <tbody>
                  {relatedBills.map((bill, index) => (
                    <tr
                      key={bill.billId}
                      className={
                        bill.billId === billId ? "active-bill-row" : ""
                      }
                    >
                      <td>
                        Bill {index + 1}
                        {bill.billId === billId && (
                          <span className="current-indicator"> (Current)</span>
                        )}
                      </td>
                      <td>{bill.date || "N/A"}</td>
                      <td>₹ {formatCurrency(bill.totalAmount)}</td>
                      <td>
                        <span
                          className={`status-pill status-${
                            bill.status?.toLowerCase() || "pending"
                          }`}
                        >
                          {bill.status === "Approved" ? (
                            <>
                              <FiCheckCircle size={12} /> Approved
                            </>
                          ) : bill.status === "Pending" ? (
                            <>
                              <FiClock size={12} /> Pending
                            </>
                          ) : bill.status === "Requested" ? (
                            <>
                              <FiAlertCircle size={12} /> Requested
                            </>
                          ) : bill.status === "Declined" ? (
                            <>
                              <FiXCircle size={12} /> Declined
                            </>
                          ) : bill.status === "Checked" ? (
                            <>
                              <FiCheck size={12} /> Checked
                            </>
                          ) : bill.status === "Paid" ? (
                            <>
                              <FiCheckCircle size={12} /> Paid
                            </>
                          ) : bill.status === "Draft" ? (
                            <>
                              <MdDrafts size={12} /> Draft
                            </>
                          ) : (
                            <>{bill.status || "Pending"}</>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="2">
                      <strong>Totals</strong>
                    </td>
                    <td>₹ {formatCurrency(billsSummary.totalAmount)}</td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
      <Modal
        open={isModalOpen}
        onClose={closeModal}
        center
        styles={modalStyles}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <h2
            style={{
              color: "#333",
              marginTop: 0,
              marginBottom: "16px",
              fontSize: "22px",
              borderBottom: "1px solid #eee",
              paddingBottom: "12px",
            }}
          >
            Confirm Bill Update
          </h2>

          <div
            style={{
              marginBottom: "24px",
            }}
          >
            <p
              style={{
                fontSize: "16px",
                lineHeight: 1.5,
                color: "#555",
                margin: 0,
              }}
            >
              Do you want to overwrite the present bill amount with the billed
              amount?
            </p>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
            }}
          >
            <button
              style={{
                padding: "10px 16px",
                borderRadius: "4px",
                border: "none",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s ease",
                backgroundColor: "#e0e0e0",
                color: "#333",
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#d0d0d0")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "#e0e0e0")}
              onClick={closeModal}
            >
              No
            </button>

            <button
              style={{
                padding: "10px 16px",
                borderRadius: "4px",
                border: "none",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s ease",
                backgroundColor: "#4a90e2",
                color: "white",
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#3a7fcf")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "#4a90e2")}
              onClick={() => {
                confirmOverwrite();
                closeModal();
              }}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        open={showModal}
        onClose={handleModalClose}
        center
        classNames={{
          modal: "custom-modal",
          overlay: "custom-overlay",
        }}
      >
        <div className="payment-modal">
          <div className="modal-header">
            <h2>Enter Payment Amount</h2>
            <button className="close-button" onClick={handleModalClose}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="amount-input-container">
            <div className="currency-label">₹</div>
            <input
              autoFocus={true}
              type="number"
              value={paidAmount}
              onChange={(e) => setPaidAmount(e.target.value)}
              placeholder="0.00"
            />
          </div>

          <div className="button-group">
            <button className="btn cancel-btn" onClick={handleModalClose}>
              Cancel
            </button>
            <button className="btn submit-btn" onClick={handlePaidAmountSubmit}>
              Mark as paid
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default drawerTemplate(RaiseBill);
